import React, { useState, useEffect, useLayoutEffect, useContext, useCallback, useMemo, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useLocation, useHistory } from "react-router-dom";
import {
  Paper,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  Box,
  FormControl,
  Select,
  InputLabel,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  FormHelperText,
  Badge,
  Popover,
  Divider,
} from "@material-ui/core";
import {
  Add as AddIcon,
  ViewColumn as ViewColumnIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PersonAdd as PersonAddIcon,
  LibraryAdd as LibraryAddIcon,
  Print as PrintIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { AuthContext } from "../../context/Auth/AuthContext";
import useKanbanSocket from "../../hooks/useKanbanSocket";
import useStableCallback from "../../hooks/useStableCallback";
import api from "../../services/api";
import {
  listBoards,
  showBoard,
  createCard,
  deleteBoard,
  deleteColumn,
  clearColumn,
  deleteCard,
  updateCard,
  moveCard,
  reorderCards,
  reorderBoards,
  reorderColumns,
  seedDefaultBoards,
  getBoardTemplates,
  searchCardsAPI,
} from "../../services/kanban";
import ImobilizadoAutocomplete, { isImobilizadoField } from "../../components/KanbanBoard/ImobilizadoAutocomplete";
import { TIPO_LOCACAO_FIELD, DATA_DEVOLUCAO_FIELD, calcularDataDevolucao, isDevolucaoEditavel } from "../../helpers/locacaoDevolucao";
import BoardModal from "../../components/KanbanBoard/BoardModal";
import ColumnModal from "../../components/KanbanBoard/ColumnModal";
import CardModal from "../../components/KanbanBoard/CardModal";
import KanbanColumn, { VALID_SORT_OPTIONS } from "../../components/KanbanBoard/KanbanColumn";
import BoardReportModal from "../../components/KanbanBoard/BoardReportModal";
import { KANBAN_COMPACT_MQ } from "../../components/KanbanBoard/compactLayout";
import { KanbanCardHeightProvider } from "../../components/KanbanBoard/cardHeight";
import useUsers from "../../hooks/useUsers";
import MonetaryInput from "../../components/MonetaryInput";

const useStyles = makeStyles((theme) => ({
  // 48px = appBarSpacer do layout. O antigo "- 100px" deixava uma faixa branca
  // de 52px abaixo do board. Abaixo de sm o root do layout vira 100vh - 56px
  // (layout/index.js), daí o 104.
  root: {
    padding: theme.spacing(3),
    paddingLeft: 0,
    height: "calc(100vh - 48px)",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 104px)",
    },
    [KANBAN_COMPACT_MQ]: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1),
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    [KANBAN_COMPACT_MQ]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  titleText: {
    color: theme.palette.text.primary,
    [KANBAN_COMPACT_MQ]: {
      fontSize: "1.25rem",
    },
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  boardFilterRight: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  boardCardFilter: {
    minWidth: 220,
    [KANBAN_COMPACT_MQ]: {
      minWidth: 180,
    },
  },
  boardSortSelect: {
    minWidth: 170,
    [KANBAN_COMPACT_MQ]: {
      minWidth: 150,
    },
  },
  boardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2),
  },
  boardCard: {
    padding: theme.spacing(3),
    cursor: "pointer",
    transition: "all 0.3s",
    position: "relative",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
    "&:hover $boardCardActions": {
      opacity: 1,
    },
  },
  boardCardActions: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    opacity: 0,
    transition: "opacity 0.3s",
    display: "flex",
    gap: theme.spacing(0.5),
  },
  boardsContainer: {
    flexGrow: 1,
    overflowY: "auto",
    paddingLeft: "15px",
  },
  columnsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    flexGrow: 1,
    paddingLeft: "15px",
    paddingBottom: theme.spacing(2),
    marginRight: theme.spacing(-3),
    "& > *:last-child": {
      marginRight: theme.spacing(3),
    },
    ...theme.scrollbarStyles,
    [KANBAN_COMPACT_MQ]: {
      gap: theme.spacing(1.5),
      paddingBottom: theme.spacing(1),
    },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: "15px",
    flexWrap: "wrap",
    [KANBAN_COMPACT_MQ]: {
      marginBottom: theme.spacing(1),
    },
  },
  filterSearchIcon: {
    color: theme.palette.type === "dark" ? "#ffffff" : theme.palette.text.secondary,
  },
  filterPopoverPaper: {
    width: 380,
    maxHeight: "70vh",
    padding: theme.spacing(2),
  },
  filterSection: {
    marginBottom: theme.spacing(2),
  },
  filterSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  filterDateRange: {
    display: "flex",
    gap: theme.spacing(1),
  },
  filterActiveChips: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  // Bloco "Dados do Relacionamento" do modal de movimentacao. Fundo fixo claro
  // deixava os campos ilegiveis no tema escuro (o MUI pinta os textos de branco).
  // action.hover e um realce sutil sobre o papel do dialog nos dois temas — um
  // cinza/preto fixo destoaria do fundo em volta.
  relationshipFields: {
    background: theme.palette.action.hover,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    // Icone nativo de calendario/relogio e preto: sem inverter ele some no dark.
    "& input::-webkit-calendar-picker-indicator": {
      filter: theme.palette.type === "dark" ? "invert(1)" : "none",
    },
  },
}));

const KANBAN_CONTACT_METHODS_SETTING_KEY = "kanbanEnabledRelationshipContactMethods";
const LEGACY_CONTACT_METHOD_LABELS = {
  VISITA: "Visita",
  ORCAMENTO: "Orçamento",
  FOLLOW_UP: "Follow-up",
  NEGOCIACAO: "Negociação",
  POS_VENDA: "Pós-venda",
  RECLAMACAO: "Reclamação",
  INDICACAO: "Indicação",
};
const DEFAULT_CONTACT_METHODS = ["Visita", "Orçamento", "Follow-up", "Negociação", "Pós-venda", "Reclamação", "Indicação"];

// ---------------------------------------------------------------------------
// Filtros do board
//
// Todos os filtros sao combinaveis: OU dentro de um mesmo campo, E entre campos
// diferentes. Cada predicado devolve a lista intacta quando sua dimensao esta
// vazia, e e isso que faz o AND valer apenas entre os filtros de fato ativos.
// ---------------------------------------------------------------------------

const KANBAN_FILTERS_VERSION = 2;

const createEmptyFilters = () => ({
  customFields: {}, // { [fieldName]: valores[] } -> OR interno
  createdAt: { start: "", end: "" },
  dueDate: { start: "", end: "" },
  tasks: { type: "all", text: "" },
  tags: [], // OR interno
});

// Campos que ja possuem seccao dedicada no popover de filtros. CreateBoardService
// semeia "dueDate" como fieldDefinition de todo board novo, e o backend copia
// customFields.dueDate para a coluna nativa — filtrar pelos dois e redundante.
const DEDICATED_FILTER_FIELD_KEYS = new Set(["duedate", "createdat"]);

const isDedicatedFilterField = (fieldName) =>
  DEDICATED_FILTER_FIELD_KEYS.has(
    String(fieldName || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase()
  );

const TASK_FILTER_LABELS = {
  all: "Todos os Cards",
  "with-tasks": "Com Tarefas",
  "without-tasks": "Sem Tarefas",
  "with-pending": "Com Tarefas Pendentes",
  "with-completed": "Com Tarefas Concluídas",
  "all-completed": "Todas Tarefas Concluídas",
};

// customFields pode chegar como objeto (caminho normal) ou como string JSON
// (ex.: cards mesclados via WebSocket). Sem este parse os filtros por campo e a
// busca geral nao enxergam os valores personalizados. Espelha o CardModal.
const getCardCustomFields = (card) => {
  const raw = card?.customFields;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_err) {
      return {};
    }
  }
  return typeof raw === "object" ? raw : {};
};

const normalizeSearchText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

// Tags do card sao gravadas como array de strings, mas relatorios antigos ja
// trouxeram objetos. Aceita as duas formas.
const normalizeTagValue = (tag) => {
  if (typeof tag === "string") return tag;
  return tag?.name ?? tag?.label ?? tag?.value ?? "";
};

// fieldOptions vem como {label, value} ou como string solta.
const getFieldOptionValue = (option) => String(option?.value ?? option?.label ?? option ?? "");
const getFieldOptionLabel = (option) => String(option?.label ?? option?.value ?? option ?? "");

const isDateRangeActive = (range) => Boolean(range?.start || range?.end);

// Descarta lixo do localStorage e campos customizados que nao existem mais no
// board (o board pode ter perdido um fieldDefinition desde o ultimo acesso).
const sanitizeFilters = (raw, fieldDefinitions = []) => {
  const base = createEmptyFilters();
  if (!raw || typeof raw !== "object") return base;

  const availableFields = new Set(
    (fieldDefinitions || [])
      .map((f) => f.fieldName)
      .filter((fieldName) => !isDedicatedFilterField(fieldName))
  );
  if (raw.customFields && typeof raw.customFields === "object") {
    Object.entries(raw.customFields).forEach(([fieldName, values]) => {
      if (!availableFields.has(fieldName)) return;
      const list = Array.isArray(values) ? values : [values];
      if (list.some((value) => value !== "" && value !== null && value !== undefined)) {
        base.customFields[fieldName] = list;
      }
    });
  }

  ["createdAt", "dueDate"].forEach((key) => {
    if (raw[key] && typeof raw[key] === "object") {
      base[key] = { start: String(raw[key].start || ""), end: String(raw[key].end || "") };
    }
  });

  if (raw.tasks && typeof raw.tasks === "object") {
    base.tasks = {
      type: TASK_FILTER_LABELS[raw.tasks.type] ? raw.tasks.type : "all",
      text: String(raw.tasks.text || ""),
    };
  }

  if (Array.isArray(raw.tags)) {
    base.tags = raw.tags.map(String).filter(Boolean);
  }

  return base;
};

// Formato antigo (um unico campo por vez) -> formato combinavel.
const migrateLegacyFilters = (saved) => {
  const migrated = createEmptyFilters();
  if (!saved || typeof saved !== "object") return migrated;

  migrated.tasks = {
    type: saved.taskFilter || "all",
    text: saved.taskTextFilter || "",
  };
  migrated.createdAt = {
    start: saved.dateFilterStart || "",
    end: saved.dateFilterEnd || "",
  };

  const { columnFilterField, columnFilterValue } = saved;
  if (
    columnFilterField &&
    columnFilterField !== "tasks" &&
    columnFilterField !== "createdAt" &&
    columnFilterValue
  ) {
    migrated.customFields = { [columnFilterField]: [columnFilterValue] };
  }

  return migrated;
};

// Compara uma data do card (string ISO) contra um intervalo [start, end] em
// dias inteiros. Card sem a data e excluido quando o intervalo esta ativo.
const matchesDateRange = (rawDate, start, end) => {
  if (!rawDate) return false;
  const cardDate = new Date(rawDate);
  if (Number.isNaN(cardDate.getTime())) return false;
  cardDate.setHours(0, 0, 0, 0);

  if (start && cardDate < new Date(`${start}T00:00:00`)) return false;
  if (end && cardDate > new Date(`${end}T23:59:59`)) return false;
  return true;
};

const formatDateRangeLabel = (range) => {
  const br = (iso) => {
    const [y, m, d] = String(iso).split("-");
    return d ? `${d}/${m}/${y}` : iso;
  };
  if (range.start && range.end) return `${br(range.start)} – ${br(range.end)}`;
  if (range.start) return `a partir de ${br(range.start)}`;
  return `até ${br(range.end)}`;
};

const normalizeSpreadsheetText = (value = "") => {
  return String(value)
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\u00a0/g, " ");
};

const insertTextAtSelection = (originalText, insertedText, selectionStart, selectionEnd) => {
  const safeStart = Number.isInteger(selectionStart) ? selectionStart : originalText.length;
  const safeEnd = Number.isInteger(selectionEnd) ? selectionEnd : safeStart;

  return `${originalText.slice(0, safeStart)}${insertedText}${originalText.slice(safeEnd)}`;
};

const normalizeContactMethodLabel = (value) => String(value || "").replace(/\s+/g, " ").trim();

const dedupeContactMethods = (items = []) => {
  const unique = [];
  const seen = new Set();

  for (const item of items) {
    const normalized = normalizeContactMethodLabel(item);
    if (!normalized) continue;

    const key = normalized
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

    if (seen.has(key)) continue;

    seen.add(key);
    unique.push(normalized);
  }

  return unique;
};

const parseEnabledContactMethods = (rawValue) => {
  if (!rawValue) {
    return DEFAULT_CONTACT_METHODS;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return DEFAULT_CONTACT_METHODS;
    }

    const normalized = dedupeContactMethods(
      parsed.map((value) => {
        const raw = normalizeContactMethodLabel(value);
        return LEGACY_CONTACT_METHOD_LABELS[String(raw).toUpperCase()] || raw;
      })
    );

    return normalized.length > 0 ? Array.from(new Set(normalized)) : DEFAULT_CONTACT_METHODS;
  } catch (_error) {
    return DEFAULT_CONTACT_METHODS;
  }
};

const getDefaultMoveFollowupData = () => ({
  assunto: "",
  descricao: "",
  data_followup: new Date().toISOString().split("T")[0],
  hora_followup: new Date().toTimeString().split(" ")[0].slice(0, 5),
  status: "pendente",
});

const KanbanBoard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  
  // Ref para o container das colunas
  const columnsContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // States
  const [boards, setBoards] = useState([]);
  const [boardSearchText, setBoardSearchText] = useState("");
  const [cardSearchResults, setCardSearchResults] = useState([]);
  const [searchingCards, setSearchingCards] = useState(false);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggingCard, setDraggingCard] = useState(null);
  const [draggingBoardId, setDraggingBoardId] = useState(null);
  const [draggingColumnId, setDraggingColumnId] = useState(null);
  const [boardMenuAnchor, setBoardMenuAnchor] = useState(null);

  // Modals
  const [boardModalOpen, setBoardModalOpen] = useState(false);
  const [boardReportOpen, setBoardReportOpen] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleMoveModalOpen, setScheduleMoveModalOpen] = useState(false);
  const [moveScheduleDate, setMoveScheduleDate] = useState("");
  const [moveScheduleDescription, setMoveScheduleDescription] = useState("");
  const [pendingMove, setPendingMove] = useState(null);
  const [requireScheduleOnMove, setRequireScheduleOnMove] = useState(false);
  const [inactivityTimerConfig, setInactivityTimerConfig] = useState({
    enabled: false,
    warningDays: 1,
    dangerDays: 3,
    criticalDays: 5,
    warningColor: "#ffeb3b",
    dangerColor: "#f44336",
    criticalColor: "#000000",
  });
  
  // Modal de motivo da movimentação
  const [moveReasonModalOpen, setMoveReasonModalOpen] = useState(false);
  const [moveReasonModalMode, setMoveReasonModalMode] = useState("move");
  const [moveReason, setMoveReason] = useState("");
  const [requiredFieldsModalOpen, setRequiredFieldsModalOpen] = useState(false);
  const [requiredFieldsToFill, setRequiredFieldsToFill] = useState([]);
  const [requiredFieldsValues, setRequiredFieldsValues] = useState({});
  const [requiredFieldsErrors, setRequiredFieldsErrors] = useState({});
  const [requiredFieldsMode, setRequiredFieldsMode] = useState("move");
  const [pendingMoveCard, setPendingMoveCard] = useState(null);
  const [pendingClonePayload, setPendingClonePayload] = useState(null);
  const [savingRequiredFields, setSavingRequiredFields] = useState(false);
  const [requiredClienteOptions, setRequiredClienteOptions] = useState([]);
  const [requiredClienteSearch, setRequiredClienteSearch] = useState("");
  const [loadingRequiredClientes, setLoadingRequiredClientes] = useState(false);
  const [requiredContatoOptions, setRequiredContatoOptions] = useState([]);
  const [loadingRequiredContatos, setLoadingRequiredContatos] = useState(false);
  const [requiredTicketOptions, setRequiredTicketOptions] = useState([]);
  const [generateRelationshipOnMove, setGenerateRelationshipOnMove] = useState(true);
  const [moveCardClienteId, setMoveCardClienteId] = useState(null);
  const [moveContatos, setMoveContatos] = useState([]);
  const [moveContatoFormOpen, setMoveContatoFormOpen] = useState(false);
  const [moveContatoFormData, setMoveContatoFormData] = useState({
    nome: "", cargo: "", telefone: "", ddd_telefone: "", celular: "",
    ddd_celular: "", email: "", email_secundario: "", whatsapp: "",
    linkedin: "", observacoes: "", principal: false, ativo: true,
  });
  const [savingMoveContato, setSavingMoveContato] = useState(false);
  const [moveRegistrarFollowup, setMoveRegistrarFollowup] = useState(false);
  const [enabledRelationshipContactMethods, setEnabledRelationshipContactMethods] = useState(DEFAULT_CONTACT_METHODS);
  const [moveRelacionamentoData, setMoveRelacionamentoData] = useState({
    data_contato: new Date().toISOString().split("T")[0],
    hora_contato: new Date().toTimeString().split(" ")[0].slice(0, 5),
    meio_contato: "",
    contatoId: "",
  });
  const [moveFollowupData, setMoveFollowupData] = useState(getDefaultMoveFollowupData());

  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneLoadingBoards, setCloneLoadingBoards] = useState(false);
  const [cloneSubmitting, setCloneSubmitting] = useState(false);
  const [cloneBoards, setCloneBoards] = useState([]);
  const [cloneColumns, setCloneColumns] = useState([]);
  const [cloneSelectedBoardId, setCloneSelectedBoardId] = useState("");
  const [cloneSelectedColumnId, setCloneSelectedColumnId] = useState("");
  const [cloneTransferMode, setCloneTransferMode] = useState("copy");
  const [pendingCloneCard, setPendingCloneCard] = useState(null);

  // Current items for editing
  const [currentColumn, setCurrentColumn] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [pendingCardToOpen, setPendingCardToOpen] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingBoard, setEditingBoard] = useState(null);
  // Todos os filtros do board vivem num objeto unico: campos customizados sao um
  // mapa dinamico (N campos ao mesmo tempo) e nenhuma dimensao exclui a outra.
  const [filters, setFilters] = useState(createEmptyFilters);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  // De qual board sao os filtros que estao em `filters`. No commit em que o board
  // troca, `filters` ainda e o do board anterior; sem esta guarda o efeito de save
  // gravaria esses filtros na chave do board novo.
  const [filtersBoardId, setFiltersBoardId] = useState(null);

  // Filtro geral de cards e ordenação geral (board-wide), substituem os controles
  // que antes existiam em cada coluna do Kanban.
  const [cardFilterText, setCardFilterText] = useState(""); // Filtro geral de texto (efêmero, não persiste)
  // Valor do filtro de texto que de fato filtra: espera a digitação parar, senão
  // cada tecla refiltrava e redesenhava o board inteiro.
  const [debouncedCardFilterText, setDebouncedCardFilterText] = useState("");
  const [boardSortOption, setBoardSortOption] = useState("order"); // Ordenação geral (persiste no localStorage)

  // Id do board aberto e controle das cargas em andamento. Refs porque os
  // handlers assíncronos precisam do valor atual depois de um await (com o
  // currentBoard da closure, uma recarga podia trazer de volta o board anterior).
  const currentBoardIdRef = useRef(null);
  const boardRequestRef = useRef({ seq: 0, switching: false });

  const normalizeFieldType = useCallback((fieldType) => {
    const normalized = String(fieldType || "text").trim().toLowerCase();

    const typeMap = {
      text: "text",
      textarea: "textarea",
      select: "select",
      "select-one": "select",
      "select_one": "select",
      "select unico": "select",
      "select-unico": "select",
      "select_unico": "select",
      radio: "radio",
      multiselect: "multiselect",
      "multi-select": "multiselect",
      "multi_select": "multiselect",
      "select multiplo": "multiselect",
      "select-multiplo": "multiselect",
      "select_multiplo": "multiselect",
      checkbox: "checkbox",
      date: "date",
      number: "number",
      email: "email",
      color: "color",
      colorpicker: "color",
      "color-picker": "color",
      "color_picker": "color",
      money: "money",
      currency: "money",
      valor: "money",
    };

    return typeMap[normalized] || "text";
  }, []);

  const normalizeFieldKey = useCallback((value) => {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();
  }, []);

  const inferFieldTypeFromSamples = useCallback(
    (fieldName, samples = []) => {
      const normalizedFieldName = normalizeFieldKey(fieldName);
      const nonEmptySamples = samples.filter(
        (sample) => sample !== null && sample !== undefined && String(sample).trim() !== ""
      );

      if (normalizedFieldName.includes("color") || normalizedFieldName.includes("cor")) {
        return "color";
      }

      if (normalizedFieldName.includes("data") || normalizedFieldName.includes("date")) {
        return "date";
      }

      if (nonEmptySamples.some((sample) => Array.isArray(sample))) {
        return "multiselect";
      }

      if (
        nonEmptySamples.length > 0 &&
        nonEmptySamples.every(
          (sample) =>
            typeof sample === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(sample.trim())
        )
      ) {
        return "color";
      }

      if (
        nonEmptySamples.length > 0 &&
        nonEmptySamples.every((sample) => typeof sample === "number")
      ) {
        if (
          normalizedFieldName.includes("valor") ||
          normalizedFieldName.includes("preco") ||
          normalizedFieldName.includes("price")
        ) {
          return "money";
        }
        return "number";
      }

      if (
        nonEmptySamples.length > 0 &&
        nonEmptySamples.every(
          (sample) =>
            typeof sample === "string" &&
            /^\d{4}-\d{2}-\d{2}$/.test(sample.trim())
        )
      ) {
        return "date";
      }

      return "text";
    },
    [normalizeFieldKey]
  );

  const sortByOrder = useCallback((items = []) => {
    return [...items].sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
  }, []);

  const getNormalizedBoardFields = useCallback((boardArg = currentBoard) => {
    const defaultFields = [
      { fieldName: "title", fieldLabel: "Titulo", fieldType: "text" },
      { fieldName: "description", fieldLabel: "Descricao", fieldType: "textarea" },
      { fieldName: "valorReais", fieldLabel: "Valor R$", fieldType: "money" },
      {
        fieldName: "priority",
        fieldLabel: "Prioridade",
        fieldType: "select",
        fieldOptions: [
          { value: "low", label: "Baixa" },
          { value: "medium", label: "Media" },
          { value: "high", label: "Alta" },
          { value: "urgent", label: "Urgente" },
        ],
      },
      { fieldName: "dueDate", fieldLabel: "Data de Vencimento", fieldType: "date" },
      { fieldName: "clienteId", fieldLabel: "Cliente", fieldType: "number" },
    ];

    const inferredCustomFields = [];
    const customFieldSamples = new Map();
    const columns = boardArg?.columns || [];

    columns.forEach((column) => {
      (column.cards || []).forEach((card) => {
        const customFields = card?.customFields || {};
        Object.keys(customFields).forEach((fieldName) => {
          if (!fieldName) return;
          if (!customFieldSamples.has(fieldName)) {
            customFieldSamples.set(fieldName, []);
          }
          customFieldSamples.get(fieldName).push(customFields[fieldName]);
        });
      });
    });

    Array.from(customFieldSamples.entries()).forEach(([fieldName, samples]) => {
      inferredCustomFields.push({
        fieldName,
        fieldLabel: fieldName
          .replace(/_/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/^./, (str) => str.toUpperCase()),
        fieldType: inferFieldTypeFromSamples(fieldName, samples),
      });
    });

    const merged = [
      ...defaultFields,
      ...(boardArg?.fieldDefinitions || []),
      ...inferredCustomFields,
    ];
    const fieldMap = new Map();

    merged.forEach((field) => {
      if (!field?.fieldName && !field?.fieldLabel) return;

      const rawKey = field.fieldName || field.fieldLabel;
      const normalizedKey = normalizeFieldKey(rawKey);
      const candidate = {
        ...field,
        fieldName: field.fieldName || rawKey,
        fieldType: normalizeFieldType(field.fieldType),
        fieldLabel: field.fieldLabel || field.fieldName || rawKey,
      };

      if (!fieldMap.has(normalizedKey)) {
        fieldMap.set(normalizedKey, candidate);
        return;
      }

      const existing = fieldMap.get(normalizedKey);
      const shouldReplaceByType = existing.fieldType === "text" && candidate.fieldType !== "text";
      const shouldReplaceByOptions =
        (!existing.fieldOptions || existing.fieldOptions.length === 0) &&
        candidate.fieldOptions &&
        candidate.fieldOptions.length > 0;

      if (shouldReplaceByType || shouldReplaceByOptions) {
        fieldMap.set(normalizedKey, {
          ...existing,
          ...candidate,
          fieldLabel: candidate.fieldLabel || existing.fieldLabel,
        });
      }
    });

    return Array.from(fieldMap.values());
  }, [
    currentBoard,
    inferFieldTypeFromSamples,
    normalizeFieldKey,
    normalizeFieldType,
  ]);

  // Campos do board aberto. getNormalizedBoardFields percorre todos os cards;
  // antes era chamado a cada render (prop do ColumnModal).
  const normalizedBoardFields = useMemo(
    () => getNormalizedBoardFields(),
    [getNormalizedBoardFields]
  );

  const resolveBoardFieldDefinition = useCallback(
    (requiredKey, boardFields) => {
      const normalizedRequiredKey = normalizeFieldKey(requiredKey);

      const exactByFieldName = boardFields.find(
        (field) => String(field.fieldName) === String(requiredKey)
      );
      if (exactByFieldName) return exactByFieldName;

      const byNormalizedFieldName = boardFields.find(
        (field) => normalizeFieldKey(field.fieldName) === normalizedRequiredKey
      );
      if (byNormalizedFieldName) return byNormalizedFieldName;

      const byNormalizedLabel = boardFields.find(
        (field) => normalizeFieldKey(field.fieldLabel) === normalizedRequiredKey
      );
      if (byNormalizedLabel) return byNormalizedLabel;

      return null;
    },
    [normalizeFieldKey]
  );

  const getCardFieldValue = useCallback((card, fieldName) => {
    const customFields = card?.customFields || {};
    if (Object.prototype.hasOwnProperty.call(customFields, fieldName)) {
      return customFields[fieldName];
    }

    const aliases = {
      clienteId: ["cliente", "CODCLI"],
      contactId: ["contactId", "contatoId", "contato"],
      ticketId: ["ticket", "ticket_id"],
      dueDate: ["due_date", "dataVencimento", "data_vencimento"],
      priority: ["prioridade"],
    };

    const fieldAliases = aliases[fieldName] || [];
    for (const alias of fieldAliases) {
      if (Object.prototype.hasOwnProperty.call(customFields, alias)) {
        return customFields[alias];
      }
    }

    const directFieldMap = {
      title: card?.title,
      description: card?.description,
      priority: card?.priority,
      dueDate: card?.dueDate,
      assignedUser: card?.assignedUserId,
      assignedUserId: card?.assignedUserId,
      ticketId: card?.ticketId,
      contactId: card?.contactId,
      clienteId: card?.clienteId,
      cliente: card?.cliente,
      contato: card?.contact,
      ticket: card?.ticket,
    };

    if (Object.prototype.hasOwnProperty.call(directFieldMap, fieldName)) {
      return directFieldMap[fieldName];
    }

    for (const alias of fieldAliases) {
      if (Object.prototype.hasOwnProperty.call(directFieldMap, alias)) {
        return directFieldMap[alias];
      }
    }

    return undefined;
  }, []);

  const isMissingRequiredFieldValue = useCallback((value) => {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "string") return value.trim() === "";
    if (typeof value === "object") {
      if (value instanceof Date) return Number.isNaN(value.getTime());
      const keys = Object.keys(value || {});
      if (keys.length === 0) return true;

      const idLike = value.id || value.CODCLI || value.codcli || value.codigo;
      if (idLike !== undefined && idLike !== null && String(idLike).trim() !== "") {
        return false;
      }

      return false;
    }
    return false;
  }, []);

  const { users, loading: loadingUsers } = useUsers();

  useEffect(() => {
    // Com ?boardId= quem carrega é o efeito da URL; a lista vem em segundo plano,
    // sem segurar o spinner nem atrasar o board.
    const hasBoardInUrl = Boolean(new URLSearchParams(location.search).get("boardId"));
    loadBoards({ silent: hasBoardInUrl });
    loadScheduleSettings();
  }, []);

  useLayoutEffect(() => {
    currentBoardIdRef.current = currentBoard ? Number(currentBoard.id) : null;
  }, [currentBoard?.id]);

  // Limpar o campo vale na hora; digitar espera 300 ms sem novas teclas.
  useEffect(() => {
    if (!cardFilterText.trim()) {
      setDebouncedCardFilterText("");
      return undefined;
    }
    const timer = setTimeout(() => setDebouncedCardFilterText(cardFilterText), 300);
    return () => clearTimeout(timer);
  }, [cardFilterText]);

  useEffect(() => {
    if (!requiredFieldsModalOpen) return;

    const uniqueTickets = new Map();
    (currentBoard?.columns || []).forEach((column) => {
      (column.cards || []).forEach((card) => {
        const ticketId = Number(card?.ticketId || card?.ticket?.id);
        if (!ticketId || uniqueTickets.has(ticketId)) return;

        uniqueTickets.set(ticketId, {
          id: ticketId,
          label: `#${ticketId}`,
        });
      });
    });

    setRequiredTicketOptions(Array.from(uniqueTickets.values()));
  }, [requiredFieldsModalOpen, currentBoard]);


  // A devolucao fica desabilitada quando o tipo nao e ESPECIAL; se ela entrar na
  // lista de obrigatorios vazia, o usuario ficaria travado sem poder digitar.
  // Preenche a partir do tipo em vigor assim que o modal abre.
  useEffect(() => {
    if (!requiredFieldsModalOpen) return;
    if (!requiredFieldsToFill.some((field) => field.fieldName === DATA_DEVOLUCAO_FIELD)) return;
    if (requiredFieldsValues[DATA_DEVOLUCAO_FIELD]) return;

    const calculada = calcularDataDevolucao(getTipoLocacaoEfetivo(), pendingMoveCard?.createdAt);
    if (!calculada) return;

    setRequiredFieldsValues((prev) =>
      prev[DATA_DEVOLUCAO_FIELD] ? prev : { ...prev, [DATA_DEVOLUCAO_FIELD]: calculada }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requiredFieldsModalOpen,
    requiredFieldsToFill,
    requiredFieldsValues[TIPO_LOCACAO_FIELD],
    requiredFieldsValues[DATA_DEVOLUCAO_FIELD],
    pendingMoveCard,
  ]);

  useEffect(() => {
    if (!requiredFieldsModalOpen) return;
    const needsClienteLookup = requiredFieldsToFill.some(
      (field) => field.fieldName === "clienteId" || field.fieldName === "contatoId"
    );
    if (!needsClienteLookup) return;

    let isActive = true;
    const timeout = setTimeout(async () => {
      try {
        setLoadingRequiredClientes(true);

        const boardClientes = [];
        const seen = new Set();
        (currentBoard?.columns || []).forEach((column) => {
          (column.cards || []).forEach((card) => {
            const cliente = card?.cliente;
            const cod = Number(cliente?.CODCLI || card?.clienteId);
            if (!cod || seen.has(cod)) return;
            seen.add(cod);

            boardClientes.push({
              CODCLI: cod,
              NOMECLI: cliente?.NOMECLI || cliente?.NOMEFANTASIA || `Cliente ${cod}`,
            });
          });
        });

        const { data } = await api.get("/clientes", {
          params: {
            searchParam: requiredClienteSearch,
            pageNumber: 1,
          },
        });

        const apiClientes = Array.isArray(data)
          ? data
          : Array.isArray(data?.clientes)
            ? data.clientes
            : [];

        const merged = [...boardClientes, ...apiClientes];
        const unique = new Map();
        merged.forEach((cliente) => {
          const cod = Number(cliente?.CODCLI || cliente?.id);
          if (!cod || unique.has(cod)) return;
          unique.set(cod, {
            ...cliente,
            CODCLI: cod,
            NOMECLI: cliente?.NOMECLI || cliente?.NOMEFANTASIA || cliente?.nome || `Cliente ${cod}`,
          });
        });

        if (isActive) {
          setRequiredClienteOptions(Array.from(unique.values()));
        }
      } catch (error) {
        console.error("Erro ao carregar clientes para campos obrigatorios:", error);
        if (isActive) {
          setRequiredClienteOptions([]);
        }
      } finally {
        if (isActive) {
          setLoadingRequiredClientes(false);
        }
      }
    }, 300);

    return () => {
      isActive = false;
      clearTimeout(timeout);
    };
  }, [
    requiredFieldsModalOpen,
    requiredFieldsToFill,
    requiredClienteSearch,
    currentBoard,
  ]);

  useEffect(() => {
    if (!requiredFieldsModalOpen) return;
    const needsContatoLookup = requiredFieldsToFill.some((field) => field.fieldName === "contatoId");
    if (!needsContatoLookup) return;

    const selectedClienteId = Number(requiredFieldsValues.clienteId || pendingMoveCard?.clienteId);
    if (!selectedClienteId) {
      setRequiredContatoOptions([]);
      return;
    }

    let isActive = true;
    const loadContatos = async () => {
      try {
        setLoadingRequiredContatos(true);
        const { data } = await api.get(`/clientes/${selectedClienteId}/contatos`);
        const contatos = Array.isArray(data) ? data : (data?.contatos || []);

        if (isActive) {
          setRequiredContatoOptions(contatos);
        }
      } catch (error) {
        console.error("Erro ao carregar contatos para campos obrigatorios:", error);
        if (isActive) {
          setRequiredContatoOptions([]);
        }
      } finally {
        if (isActive) {
          setLoadingRequiredContatos(false);
        }
      }
    };

    loadContatos();

    return () => {
      isActive = false;
    };
  }, [requiredFieldsModalOpen, requiredFieldsToFill, requiredFieldsValues.clienteId, pendingMoveCard]);

  const getKanbanFiltersStorageKey = (boardId) =>
    `kanbanFilters:${user?.companyId || "default"}:${user?.id}:${boardId}`;

  // Ordenação geral do board (uma chave por board, sem columnId). O filtro de texto
  // geral é efêmero e não é persistido.
  const getKanbanSortStorageKey = (boardId) =>
    `kanbanSort:${user?.companyId || "default"}:${user?.id}:${boardId}`;

  useEffect(() => {
    if (!currentBoard?.id) return;

    // Filtro de texto geral não persiste: ao trocar de board, sempre reseta.
    setCardFilterText("");
    setDebouncedCardFilterText("");

    try {
      const raw = localStorage.getItem(getKanbanFiltersStorageKey(currentBoard.id));
      const saved = raw ? JSON.parse(raw) : null;
      const restored =
        saved?.version === KANBAN_FILTERS_VERSION
          ? saved.filters
          : migrateLegacyFilters(saved);

      // Sem nada salvo, sanitizeFilters devolve os filtros vazios — o que tambem
      // impede que os filtros do board anterior vazem para este.
      setFilters(sanitizeFilters(restored, currentBoard.fieldDefinitions));
    } catch (err) {
      console.error("Erro ao carregar filtros do kanban:", err);
      setFilters(createEmptyFilters());
    } finally {
      setFiltersBoardId(currentBoard.id);
    }
  }, [currentBoard?.id]);

  useEffect(() => {
    if (!currentBoard?.id) return;
    // Só grava depois que os filtros deste board foram carregados.
    if (filtersBoardId !== currentBoard.id) return;

    try {
      localStorage.setItem(
        getKanbanFiltersStorageKey(currentBoard.id),
        JSON.stringify({ version: KANBAN_FILTERS_VERSION, filters })
      );
    } catch (err) {
      console.error("Erro ao salvar filtros do kanban:", err);
    }
  }, [currentBoard?.id, filtersBoardId, filters]);

  // Carrega a ordenação geral salva deste board (ou volta ao padrão "order" para não
  // vazar a seleção de outro board).
  useEffect(() => {
    if (!currentBoard?.id) return;

    try {
      const saved = localStorage.getItem(getKanbanSortStorageKey(currentBoard.id));
      setBoardSortOption(saved && VALID_SORT_OPTIONS.includes(saved) ? saved : "order");
    } catch (err) {
      setBoardSortOption("order");
    }
  }, [currentBoard?.id]);

  // Persiste a ordenação geral sempre que muda.
  useEffect(() => {
    if (!currentBoard?.id) return;

    try {
      localStorage.setItem(getKanbanSortStorageKey(currentBoard.id), boardSortOption);
    } catch (err) {
      console.error("Erro ao salvar ordenação do kanban:", err);
    }
  }, [currentBoard?.id, boardSortOption]);

  const loadScheduleSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      const scheduleSetting = data.find(s => s.key === "requireScheduleOnCardCreation");
      setRequireScheduleOnMove(scheduleSetting?.value === "enabled");

      const getSettingValue = (key, fallback) => {
        const setting = data.find((s) => s.key === key);
        return setting?.value ?? fallback;
      };

      const parseDays = (value, fallback) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
      };

      setInactivityTimerConfig({
        enabled: String(getSettingValue("kanbanInactivityHighlightEnabled", "disabled")) === "enabled",
        warningDays: parseDays(getSettingValue("kanbanInactivityWarningDays", 1), 1),
        dangerDays: parseDays(getSettingValue("kanbanInactivityDangerDays", 3), 3),
        criticalDays: parseDays(getSettingValue("kanbanInactivityCriticalDays", 5), 5),
        warningColor: String(getSettingValue("kanbanInactivityWarningColor", "#ffeb3b")),
        dangerColor: String(getSettingValue("kanbanInactivityDangerColor", "#f44336")),
        criticalColor: String(getSettingValue("kanbanInactivityCriticalColor", "#000000")),
      });

      const enabledMethodsValue = getSettingValue(
        KANBAN_CONTACT_METHODS_SETTING_KEY,
        JSON.stringify(DEFAULT_CONTACT_METHODS)
      );
      setEnabledRelationshipContactMethods(parseEnabledContactMethods(enabledMethodsValue));
    } catch (err) {
      console.error("Erro ao carregar configurações:", err);
      setRequireScheduleOnMove(false);
      setEnabledRelationshipContactMethods(DEFAULT_CONTACT_METHODS);
    }
  };

  // Carrega o board da URL. Não espera a lista de boards: antes o board só era
  // pedido depois que a lista inteira chegava (as duas em cascata).
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const boardId = searchParams.get('boardId');

    if (boardId && Number(currentBoard?.id) !== Number(boardId)) {
      loadBoard(boardId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, currentBoard?.id]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const targetBoardId = Number(searchParams.get("boardId"));
    const targetColumnId = searchParams.get("columnId");
    const targetCardId = searchParams.get("cardId");

    if (!currentBoard || Number(currentBoard.id) !== targetBoardId) {
      return;
    }

    if (!targetColumnId && !targetCardId) {
      return;
    }

    const focusTarget = () => {
      const cardElement = targetCardId
        ? document.getElementById(`kanban-card-${targetCardId}`)
        : null;
      const columnElement = targetColumnId
        ? document.getElementById(`kanban-column-${targetColumnId}`)
        : null;
      const targetElement = cardElement || columnElement;

      if (!targetElement) {
        return;
      }

      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });

      const previousOutline = targetElement.style.outline;
      const previousOutlineOffset = targetElement.style.outlineOffset;

      targetElement.style.outline = "2px solid #1976d2";
      targetElement.style.outlineOffset = "2px";

      window.setTimeout(() => {
        targetElement.style.outline = previousOutline;
        targetElement.style.outlineOffset = previousOutlineOffset;
      }, 1600);
    };

    const firstAttempt = window.setTimeout(focusTarget, 120);
    const secondAttempt = window.setTimeout(focusTarget, 700);

    return () => {
      window.clearTimeout(firstAttempt);
      window.clearTimeout(secondAttempt);
    };
    // Só o id: as recargas agora não desmontam o board, e com o objeto inteiro
    // nas deps cada recarga rolava a tela de volta até o card do link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBoard?.id, location.search]);
  
  // Auto-scroll quando arrastar card próximo às bordas
  useEffect(() => {
    if (!draggingCard || !columnsContainerRef.current) {
      // Limpar intervalo se não estiver arrastando
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      return;
    }

    const container = columnsContainerRef.current;
    const scrollSpeed = 15; // Pixels por frame
    const edgeSize = 100; // Área de detecção próxima às bordas (pixels)

    const handleMouseMove = (e) => {
      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX;
      
      // Calcular distância das bordas
      const distanceFromLeft = mouseX - containerRect.left;
      const distanceFromRight = containerRect.right - mouseX;
      
      // Limpar intervalo anterior se existir
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      
      // Scroll para a esquerda
      if (distanceFromLeft < edgeSize && distanceFromLeft > 0) {
        scrollIntervalRef.current = setInterval(() => {
          if (container.scrollLeft > 0) {
            container.scrollLeft -= scrollSpeed;
          }
        }, 16); // ~60fps
      }
      // Scroll para a direita
      else if (distanceFromRight < edgeSize && distanceFromRight > 0) {
        scrollIntervalRef.current = setInterval(() => {
          if (container.scrollLeft < container.scrollWidth - container.clientWidth) {
            container.scrollLeft += scrollSpeed;
          }
        }, 16); // ~60fps
      }
    };

    const handleMouseUp = () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };

    // Adicionar listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('dragend', handleMouseUp);

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('dragend', handleMouseUp);
      
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [draggingCard]);

  // summary: a grade só usa nome/descrição/permissões e as contagens; a lista
  // completa trazia todos os cards de todos os boards. `silent` não liga o
  // spinner (que desmonta a tela inteira).
  const loadBoards = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await listBoards({ summary: true });
      setBoards(data.boards);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) {
        setBoards([]);
        setCurrentBoard(null);
        toast.error("Voce nao tem permissao para acessar os fluxos.");
        history.push("/");
      } else {
        toast.error("Erro ao carregar boards");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [history]);

  const loadCloneBoards = useCallback(async () => {
    try {
      setCloneLoadingBoards(true);
      const { data } = await listBoards({ isActive: true });
      const availableBoards = sortByOrder(data?.boards || []).filter(
        (board) => Number(board.id) !== Number(currentBoard?.id)
      );

      setCloneBoards(availableBoards);

      if (availableBoards.length === 0) {
        setCloneSelectedBoardId("");
        setCloneColumns([]);
        setCloneSelectedColumnId("");
        return;
      }

      setCloneSelectedBoardId((currentValue) => {
        const hasCurrentBoard = availableBoards.some(
          (board) => Number(board.id) === Number(currentValue)
        );
        return hasCurrentBoard ? currentValue : availableBoards[0].id;
      });
    } catch (error) {
      console.error("Erro ao carregar boards para envio de card:", error);
      toast.error("Erro ao carregar boards para envio");
      setCloneBoards([]);
      setCloneSelectedBoardId("");
      setCloneColumns([]);
      setCloneSelectedColumnId("");
    } finally {
      setCloneLoadingBoards(false);
    }
  }, [currentBoard?.id, sortByOrder]);

  useEffect(() => {
    if (!cloneModalOpen) return;
    loadCloneBoards();
  }, [cloneModalOpen, loadCloneBoards]);

  useEffect(() => {
    if (!cloneSelectedBoardId) {
      setCloneColumns([]);
      setCloneSelectedColumnId("");
      return;
    }

    const selectedBoard = cloneBoards.find(
      (board) => Number(board.id) === Number(cloneSelectedBoardId)
    );
    const sortedColumns = sortByOrder(selectedBoard?.columns || []);

    setCloneColumns(sortedColumns);
    setCloneSelectedColumnId((currentValue) => {
      const hasCurrentColumn = sortedColumns.some(
        (column) => Number(column.id) === Number(currentValue)
      );
      return hasCurrentColumn ? currentValue : sortedColumns[0]?.id || "";
    });
  }, [cloneSelectedBoardId, cloneBoards, sortByOrder]);

  useEffect(() => {
    const trimmed = boardSearchText.trim();
    if (!trimmed || trimmed.length < 2) {
      setCardSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setSearchingCards(true);
        const { data } = await searchCardsAPI({ searchParam: trimmed });
        setCardSearchResults(data.cards || []);
      } catch (e) {
        setCardSearchResults([]);
      } finally {
        setSearchingCards(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [boardSearchText]);

  const resetCloneModalState = () => {
    setCloneModalOpen(false);
    setCloneLoadingBoards(false);
    setCloneSubmitting(false);
    setCloneBoards([]);
    setCloneColumns([]);
    setCloneSelectedBoardId("");
    setCloneSelectedColumnId("");
    setCloneTransferMode("copy");
    setPendingCloneCard(null);
  };

  const handleOpenCloneCardModal = (card) => {
    setPendingCloneCard(card);
    setCloneModalOpen(true);
  };

  const getValueFromClonePayload = (payload, fieldName) => {
    if (Object.prototype.hasOwnProperty.call(payload || {}, fieldName)) {
      return payload[fieldName];
    }

    const customFields = payload?.customFields || {};
    if (Object.prototype.hasOwnProperty.call(customFields, fieldName)) {
      return customFields[fieldName];
    }

    return undefined;
  };

  const handleSubmitCloneCard = async () => {
    if (!pendingCloneCard) {
      toast.error("Card de origem nao encontrado");
      return;
    }

    if (!cloneSelectedBoardId || !cloneSelectedColumnId) {
      toast.warning("Selecione um board e uma coluna");
      return;
    }

    if (Number(cloneSelectedBoardId) === Number(currentBoard?.id)) {
      toast.warning("Nao e permitido enviar para o mesmo board");
      return;
    }

    const targetBoard = cloneBoards.find(
      (board) => Number(board.id) === Number(cloneSelectedBoardId)
    );
    const targetColumn = (targetBoard?.columns || []).find(
      (column) => Number(column.id) === Number(cloneSelectedColumnId)
    );

    if (!targetBoard || !targetColumn) {
      toast.error("Destino de envio invalido");
      return;
    }

    setCloneSubmitting(true);

    try {
      const sourceBoardFields = getNormalizedBoardFields(currentBoard);
      const targetBoardFields = getNormalizedBoardFields(targetBoard);
      const copiedCustomFields = {};

      const sourceFieldCandidates = new Set([
        ...Object.keys(pendingCloneCard.customFields || {}),
        ...sourceBoardFields.map((field) => field.fieldName).filter(Boolean),
      ]);

      sourceFieldCandidates.forEach((sourceFieldName) => {
        const sourceDefinition =
          resolveBoardFieldDefinition(sourceFieldName, sourceBoardFields) ||
          ({ fieldName: sourceFieldName, fieldLabel: sourceFieldName, fieldType: "text" });

        const sourceValue = getCardFieldValue(pendingCloneCard, sourceDefinition.fieldName);
        if (isMissingRequiredFieldValue(sourceValue)) {
          return;
        }

        const mappedTargetDefinition =
          resolveBoardFieldDefinition(sourceDefinition.fieldName, targetBoardFields) ||
          resolveBoardFieldDefinition(sourceDefinition.fieldLabel, targetBoardFields);

        if (!mappedTargetDefinition?.fieldName) {
          return;
        }

        copiedCustomFields[mappedTargetDefinition.fieldName] = sourceValue;
      });

      const clonePayload = {
        boardId: targetBoard.id,
        columnId: targetColumn.id,
        sourceCardId: pendingCloneCard.id,
        title: pendingCloneCard.title || pendingCloneCard.customFields?.title || "Card sem titulo",
        description: pendingCloneCard.description || pendingCloneCard.customFields?.description || "",
        customFields: copiedCustomFields,
      };

      if (pendingCloneCard.tags) {
        clonePayload.tags = pendingCloneCard.tags;
      }

      if (pendingCloneCard.attachments) {
        clonePayload.attachments = pendingCloneCard.attachments;
      }

      if (Array.isArray(pendingCloneCard.tasks) && pendingCloneCard.tasks.length > 0) {
        clonePayload.tasks = pendingCloneCard.tasks;
      }

      const directFields = [
        "priority",
        "dueDate",
        "assignedUserId",
        "ticketId",
        "contactId",
        "clienteId",
        "saleUserId",
        "scheduleDate",
      ];

      directFields.forEach((fieldName) => {
        const value = getCardFieldValue(pendingCloneCard, fieldName);
        if (!isMissingRequiredFieldValue(value)) {
          clonePayload[fieldName] = value;
        }
      });

      const requiredFieldsMap = new Map();

      const addRequiredField = (requiredKey, source = "column") => {
        if (!requiredKey) return;

        const resolvedDefinition = resolveBoardFieldDefinition(requiredKey, targetBoardFields);
        const resolvedFieldName = resolvedDefinition?.fieldName || requiredKey;
        const normalizedRequiredField = normalizeFieldKey(resolvedFieldName);

        if (requiredFieldsMap.has(normalizedRequiredField)) {
          return;
        }

        requiredFieldsMap.set(normalizedRequiredField, {
          ...(resolvedDefinition || {
            fieldName: resolvedFieldName,
            fieldLabel: requiredKey,
            fieldType: "text",
          }),
          fieldName: resolvedFieldName,
          originalRequiredKey: requiredKey,
          requiredSource: source,
        });
      };

      const columnRequiredFieldNames = Array.isArray(targetColumn.requiredFieldNames)
        ? targetColumn.requiredFieldNames
        : [];

      columnRequiredFieldNames.forEach((requiredKey) => {
        addRequiredField(requiredKey, "column");
      });

      const destinationFieldDefinitions = Array.isArray(targetBoard?.fieldDefinitions)
        ? targetBoard.fieldDefinitions
        : [];

      destinationFieldDefinitions.forEach((field) => {
        const isRequired =
          field?.isRequired === true ||
          String(field?.isRequired || "").toLowerCase() === "true" ||
          Number(field?.isRequired) === 1;

        if (isRequired) {
          addRequiredField(field.fieldName || field.fieldLabel, "board");
        }
      });

      const requiredFields = Array.from(requiredFieldsMap.values());

      if (requiredFields.length > 0) {
        const missingFields = requiredFields
          .map((requiredField) => {
            const currentValue = getValueFromClonePayload(clonePayload, requiredField.fieldName);
            if (isMissingRequiredFieldValue(currentValue)) {
              return requiredField;
            }

            return null;
          })
          .filter(Boolean);

        if (missingFields.length > 0) {
          const initialValues = {};

          missingFields.forEach((field) => {
            const currentValue = getValueFromClonePayload(clonePayload, field.fieldName);
            if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
              initialValues[field.fieldName] = Array.isArray(currentValue) ? currentValue : [];
            } else {
              initialValues[field.fieldName] = currentValue ?? "";
            }
          });

          setPendingClonePayload({
            ...clonePayload,
            transferMode: cloneTransferMode,
          });
          setPendingMoveCard(pendingCloneCard);
          setRequiredFieldsMode("clone");
          setRequiredFieldsToFill(missingFields);
          setRequiredFieldsValues(initialValues);
          setRequiredFieldsErrors({});
          setRequiredFieldsModalOpen(true);
          resetCloneModalState();
          return;
        }
      }

      await startPostValidationCloneFlow(clonePayload, pendingCloneCard, cloneTransferMode);
    } catch (error) {
      console.error("Erro ao enviar card:", error);
      toast.error(error.response?.data?.message || "Erro ao enviar card para outro board");
    } finally {
      setCloneSubmitting(false);
    }
  };

  const [seedingBoards, setSeedingBoards] = useState(false);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = async () => {
    try {
      const { data } = await getBoardTemplates();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenTemplateMenu = (event) => {
    setTemplateMenuAnchor(event.currentTarget);
    if (templates.length === 0) {
      loadTemplates();
    }
  };

  const handleSeedTemplate = async (boardName) => {
    setTemplateMenuAnchor(null);
    try {
      setSeedingBoards(true);
      const { data } = await seedDefaultBoards(boardName);
      const created = data.results?.filter(r => r.created) || [];
      if (created.length === 0) {
        toast.info(`O board "${boardName}" já existe.`);
      } else {
        toast.success(`Board criado: ${created.map(r => r.boardName).join(", ")}`);
        await loadBoards();
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar board a partir do template");
    } finally {
      setSeedingBoards(false);
    }
  };

  const handleSeedDefaultBoards = async () => {
    try {
      setSeedingBoards(true);
      const { data } = await seedDefaultBoards();
      const created = data.results?.filter(r => r.created) || [];
      if (created.length === 0) {
        toast.info("Os boards padrão já existem para esta empresa.");
      } else {
        toast.success(
          `${created.length} board(s) criados: ${created.map(r => r.boardName).join(", ")}`
        );
        await loadBoards();
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar boards padrão");
    } finally {
      setSeedingBoards(false);
    }
  };

  // Troca de board (silent=false) mostra o spinner. Recarga do board aberto
  // (silent=true) atualiza no lugar: antes cada movimentação, tarefa salva ou
  // exclusão desmontava e remontava todos os cards.
  const loadBoard = useCallback(async (boardId, { silent = false } = {}) => {
    const request = boardRequestRef.current;
    // Uma troca em andamento já vai trazer o board novo.
    if (silent && request.switching) return;

    const seq = ++request.seq;
    if (!silent) {
      request.switching = true;
      setLoading(true);
    }

    // Só a carga mais recente vale; uma silenciosa ainda exige o mesmo board aberto.
    const isStale = () =>
      seq !== request.seq ||
      (silent && Number(currentBoardIdRef.current) !== Number(boardId));

    try {
      const { data } = await showBoard(boardId);
      if (isStale()) return;
      setCurrentBoard(data);
    } catch (err) {
      if (isStale()) return;
      console.error(err);
      if (err?.response?.status === 403) {
        setCurrentBoard(null);
        toast.error("Voce nao tem permissao para acessar este fluxo.");
        history.push("/kanban-board");
      } else {
        toast.error("Erro ao carregar board");
      }
    } finally {
      if (!silent && seq === request.seq) {
        request.switching = false;
        setLoading(false);
      }
    }
  }, [history]);

  const reloadCurrentBoard = useCallback(() => {
    const boardId = currentBoardIdRef.current;
    return boardId ? loadBoard(boardId, { silent: true }) : Promise.resolve();
  }, [loadBoard]);

  useEffect(() => {
    if (!pendingCardToOpen || !currentBoard) return;
    for (const column of currentBoard.columns || []) {
      const card = (column.cards || []).find(c => c.id === pendingCardToOpen);
      if (card) {
        setCurrentCard(card);
        setCardModalOpen(true);
        break;
      }
    }
    setPendingCardToOpen(null);
  }, [currentBoard, pendingCardToOpen]);

  // Callback para atualizar board via WebSocket. Função comum: o hook recebe a
  // versão estável (onSocketUpdate) e não re-assina os listeners a cada render.
  const handleSocketUpdate = (event) => {
    switch (event.type) {
      case "board":
        if (event.action === "update" && currentBoard && event.board?.id === currentBoard.id) {
          setCurrentBoard((prev) => ({
            ...prev,
            ...event.board,
          }));
          toast.info("Board atualizado por outro usuário");
        } else if (event.action === "delete" && currentBoard && event.boardId === currentBoard.id) {
          toast.warning("Este board foi excluído por outro usuário");
          setCurrentBoard(null);
          loadBoards({ silent: true });
        } else if (event.action === "create") {
          loadBoards({ silent: true });
          toast.info("Novo board criado");
        } else if (event.action === "seed") {
          loadBoards({ silent: true });
        }
        break;

      case "column":
        if (currentBoard && event.column?.kanbanBoardId === currentBoard.id) {
          if (event.action === "create") {
            setCurrentBoard((prev) => ({
              ...prev,
              columns: [...prev.columns, event.column].sort((a, b) => a.order - b.order),
            }));
            toast.info("Nova coluna adicionada");
          } else if (event.action === "update") {
            setCurrentBoard((prev) => ({
              ...prev,
              columns: prev.columns.map((col) =>
                col.id === event.column.id ? { ...col, ...event.column } : col
              ),
            }));
          } else if (event.action === "delete") {
            setCurrentBoard((prev) => ({
              ...prev,
              columns: prev.columns.filter((col) => col.id !== event.columnId),
            }));
            toast.warning("Coluna removida por outro usuário");
          }
        }
        break;

      case "columnsReordered":
        if (currentBoard && event.boardId === currentBoard.id) {
          setCurrentBoard((prev) => ({
            ...prev,
            columns: event.columns.sort((a, b) => a.order - b.order),
          }));
        }
        break;

      case "boardsReordered":
        if (event.boards?.length) {
          setBoards((prev) =>
            prev
              .map((board) => {
                const updated = event.boards.find((b) => b.id === board.id);
                return updated ? { ...board, order: updated.order } : board;
              })
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          );
        }
        break;

      case "card":
        if (currentBoard) {
          // Verificar se o card pertence ao board atual
          const cardBelongsToBoard = 
            event.card?.boardId === currentBoard.id || 
            event.card?.column?.boardId === currentBoard.id;

          if (cardBelongsToBoard) {
            if (event.action === "create") {
              setCurrentBoard((prev) => ({
                ...prev,
                columns: prev.columns.map((col) =>
                  col.id === event.card.columnId
                    ? {
                        ...col,
                        cards: [...(col.cards || []), event.card].sort((a, b) => a.order - b.order),
                      }
                    : col
                ),
              }));
              toast.success("Novo card adicionado");
            } else if (event.action === "update") {
              // Só a coluna que contém o card ganha objeto novo; as demais ficam
              // iguais e o memo das colunas evita redesenhá-las.
              setCurrentBoard((prev) => ({
                ...prev,
                columns: prev.columns.map((col) =>
                  (col.cards || []).some((card) => card.id === event.card.id)
                    ? {
                        ...col,
                        cards: col.cards.map((card) =>
                          card.id === event.card.id ? { ...card, ...event.card } : card
                        ),
                      }
                    : col
                ),
              }));
            } else if (event.action === "delete") {
              reloadCurrentBoard();
              toast.warning("Card removido por outro usuário");
            }
          }
        }
        break;

      case "cardMoved":
        if (currentBoard && event.boardId === currentBoard.id) {
          // Recarregar o board para ter as ordens atualizadas
          reloadCurrentBoard();
          toast.info("Card movido por outro usuário");
        }
        break;

      case "cardsReordered":
        if (currentBoard && event.boardId === currentBoard.id) {
          const orderById = new Map(
            (event.cards || []).map((c) => [c.id, c.order])
          );
          setCurrentBoard((prev) => ({
            ...prev,
            columns: prev.columns.map((col) => {
              if (col.id !== event.columnId) return col;
              const newCards = (col.cards || [])
                .map((c) =>
                  orderById.has(c.id)
                    ? { ...c, order: orderById.get(c.id) }
                    : c
                )
                .sort((a, b) => a.order - b.order);
              return { ...col, cards: newCards };
            }),
          }));
        }
        break;

      default:
        break;
    }
  };

  // Integrar WebSocket
  const onSocketUpdate = useStableCallback(handleSocketUpdate);
  useKanbanSocket(currentBoard?.id, onSocketUpdate);

  // O efeito da URL carrega o board; chamar loadBoard aqui também pedia o
  // board duas vezes. Exceção: a URL já é deste board (carga anterior falhou),
  // então o push não muda nada e o efeito não roda.
  const handleBoardClick = (board) => {
    const urlBoardId = new URLSearchParams(location.search).get("boardId");
    if (Number(urlBoardId) === Number(board.id)) {
      loadBoard(board.id);
      return;
    }
    history.push(`/kanban-board?boardId=${board.id}`);
  };

  const handleBackToBoards = () => {
    // Descarta cargas do board ainda em andamento (inclusive uma troca, que
    // senão deixaria o spinner ligado).
    boardRequestRef.current.seq += 1;
    boardRequestRef.current.switching = false;
    setLoading(false);
    setCurrentBoard(null);
    // Limpar o parâmetro boardId da URL
    history.push("/kanban-board");
    loadBoards({ silent: boards.length > 0 });
  };

  const handleBoardDragStart = (boardId, event) => {
    setDraggingBoardId(boardId);
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  };

  const handleBoardDragEnd = () => {
    setDraggingBoardId(null);
  };

  const handleBoardDragOver = (event) => {
    event.preventDefault();
  };

  const handleBoardDrop = async (targetBoardId, event) => {
    event.preventDefault();
    if (!draggingBoardId || draggingBoardId === targetBoardId) {
      return;
    }

    const orderedBoards = [...boards].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const sourceIndex = orderedBoards.findIndex((b) => b.id === draggingBoardId);
    const targetIndex = orderedBoards.findIndex((b) => b.id === targetBoardId);

    if (sourceIndex === -1 || targetIndex === -1) {
      setDraggingBoardId(null);
      return;
    }

    const reordered = [...orderedBoards];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const updatedBoards = reordered.map((board, index) => ({
      ...board,
      order: index
    }));

    setBoards(updatedBoards);
    setDraggingBoardId(null);

    try {
      await reorderBoards(updatedBoards.map((board) => ({
        id: board.id,
        order: board.order
      })));
    } catch (err) {
      console.error(err);
      toast.error("Erro ao reordenar boards");
      loadBoards();
    }
  };

  const handleColumnDragStart = (columnId, event) => {
    if (draggingCard) return;
    setDraggingColumnId(columnId);
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  };

  const handleColumnDragEnd = () => {
    setDraggingColumnId(null);
  };

  const handleColumnDragOver = (event) => {
    if (!draggingColumnId) return;
    event.preventDefault();
  };

  const handleColumnDrop = async (targetColumnId, event) => {
    if (!draggingColumnId || !currentBoard) {
      return;
    }
    event.preventDefault();
    if (draggingColumnId === targetColumnId) {
      setDraggingColumnId(null);
      return;
    }

    const orderedColumns = [...currentBoard.columns].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const sourceIndex = orderedColumns.findIndex((col) => col.id === draggingColumnId);
    const targetIndex = orderedColumns.findIndex((col) => col.id === targetColumnId);

    if (sourceIndex === -1 || targetIndex === -1) {
      setDraggingColumnId(null);
      return;
    }

    const reordered = [...orderedColumns];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const updatedColumns = reordered.map((col, index) => ({
      ...col,
      order: index
    }));

    setCurrentBoard((prev) => ({
      ...prev,
      columns: prev.columns.map((col) => {
        const updated = updatedColumns.find((item) => item.id === col.id);
        return updated ? { ...col, order: updated.order } : col;
      })
    }));
    setDraggingColumnId(null);

    try {
      await reorderColumns(
        currentBoard.id,
        updatedColumns.map((col) => ({
          id: col.id,
          order: col.order
        }))
      );
    } catch (err) {
      console.error(err);
      toast.error("Erro ao reordenar colunas");
      reloadCurrentBoard();
    }
  };

  const handleAddBoard = () => {
    setEditingBoard(null);
    setBoardModalOpen(true);
  };

  const handleEditBoard = (board, e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingBoard(board);
    setBoardModalOpen(true);
  };

  const handleDeleteBoardClick = (board, e) => {
    if (e) {
      e.stopPropagation();
    }
    setItemToDelete({ type: "board", ...board });
    setDeleteDialogOpen(true);
  };

  const handleBoardMenuOpen = (e) => {
    e.stopPropagation();
    setBoardMenuAnchor(e.currentTarget);
  };

  const handleBoardMenuClose = () => {
    setBoardMenuAnchor(null);
  };

  const handleEditCurrentBoard = () => {
    setEditingBoard(currentBoard);
    setBoardModalOpen(true);
    handleBoardMenuClose();
  };

  const handleDeleteCurrentBoard = () => {
    setItemToDelete({ type: "board", ...currentBoard });
    setDeleteDialogOpen(true);
    handleBoardMenuClose();
  };

  const handleAddColumn = () => {
    setCurrentColumn(null);
    setColumnModalOpen(true);
  };

  const handleAddCard = (column) => {
    setCurrentColumn(column);
    setCurrentCard(null);
    setCardModalOpen(true);
  };

  const handleEditCard = (card) => {
    setCurrentCard(card);
    setCurrentColumn(null);
    setCardModalOpen(true);
  };

  const handleEditColumn = (column) => {
    setCurrentColumn(column);
    setColumnModalOpen(true);
  };

  const handleDeleteBoard = async () => {
    try {
      await deleteBoard(itemToDelete.id);
      toast.success("Board excluído com sucesso!");
      setDeleteDialogOpen(false);
      if (currentBoard && currentBoard.id === itemToDelete.id) {
        setCurrentBoard(null);
      }
      loadBoards();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao excluir board");
    }
  };

  const handleDeleteColumn = async (column) => {
    setItemToDelete({ type: "column", ...column });
    setDeleteDialogOpen(true);
  };

  const handleClearColumn = async (column) => {
    setItemToDelete({ type: "clear-column", ...column });
    setDeleteDialogOpen(true);
  };

  const handleDeleteCard = async (card) => {
    setItemToDelete({ type: "card", ...card });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (itemToDelete.type === "board") {
        await handleDeleteBoard();
      } else if (itemToDelete.type === "column") {
        await deleteColumn(itemToDelete.id);
        toast.success("Coluna excluída com sucesso!");
        setDeleteDialogOpen(false);
        reloadCurrentBoard();
      } else if (itemToDelete.type === "clear-column") {
        await clearColumn(itemToDelete.id);
        toast.success("Coluna limpa com sucesso! Todos os cards foram excluídos.");
        setDeleteDialogOpen(false);
        reloadCurrentBoard();
      } else if (itemToDelete.type === "card") {
        await deleteCard(itemToDelete.id);
        toast.success("Card excluído com sucesso!");
        setDeleteDialogOpen(false);
        reloadCurrentBoard();
      }
    } catch (err) {
      console.error(err);
      const itemType = itemToDelete.type === "board" ? "board" : 
                       itemToDelete.type === "column" ? "coluna" :
                       itemToDelete.type === "clear-column" ? "coluna" : "card";
      toast.error(`Erro ao ${itemToDelete.type === "clear-column" ? "limpar" : "excluir"} ${itemType}`);
    }
  };

  const startPostValidationMoveFlow = async (movedCard, movePayload) => {
    const clienteId = movedCard?.clienteId ? Number(movedCard.clienteId) : null;

    setPendingMove(movePayload);
    setPendingClonePayload(null);
    setMoveReasonModalMode("move");
    setMoveReason("");
    setGenerateRelationshipOnMove(true);
    setMoveCardClienteId(clienteId);
    setMoveRelacionamentoData({
      data_contato: new Date().toISOString().split("T")[0],
      hora_contato: new Date().toTimeString().split(" ")[0].slice(0, 5),
      meio_contato: "",
      contatoId: movedCard?.contactId ? Number(movedCard.contactId) : "",
    });
    setMoveRegistrarFollowup(false);
    setMoveFollowupData(getDefaultMoveFollowupData());

    if (clienteId) {
      try {
        const { data } = await api.get(`/clientes/${clienteId}/contatos`);
        const contatosData = Array.isArray(data) ? data : (data.contatos || []);
        setMoveContatos(contatosData);
      } catch (error) {
        console.error("Erro ao carregar contatos para movimentação:", error);
        setMoveContatos([]);
      }
    } else {
      // Sem cliente o relacionamento fica vinculado ao card; oferecer o contato do atendimento
      setMoveContatos(
        movedCard?.contactId
          ? [{ id: Number(movedCard.contactId), nome: movedCard?.contact?.name || "Contato do atendimento" }]
          : []
      );
    }

    if (requireScheduleOnMove) {
      setMoveScheduleDate("");
      setMoveScheduleDescription("");
      setScheduleMoveModalOpen(true);
    } else {
      setMoveReasonModalOpen(true);
    }
  };

  const startPostValidationCloneFlow = async (clonePayload, sourceCard, transferMode = "copy") => {
    const clienteId = clonePayload?.clienteId ? Number(clonePayload.clienteId) : null;
    const contatoId = clonePayload?.contactId || sourceCard?.contactId || "";

    setPendingMove(null);
    setPendingClonePayload({
      ...clonePayload,
      transferMode,
    });
    setMoveReasonModalMode("clone");
    setMoveReason("");
    setGenerateRelationshipOnMove(true);
    setMoveCardClienteId(clienteId);
    setMoveRelacionamentoData({
      data_contato: new Date().toISOString().split("T")[0],
      hora_contato: new Date().toTimeString().split(" ")[0].slice(0, 5),
      meio_contato: "",
      contatoId: contatoId ? Number(contatoId) : "",
    });
    setMoveRegistrarFollowup(false);
    setMoveFollowupData(getDefaultMoveFollowupData());

    if (clienteId) {
      try {
        const { data } = await api.get(`/clientes/${clienteId}/contatos`);
        const contatosData = Array.isArray(data) ? data : (data.contatos || []);
        setMoveContatos(contatosData);
      } catch (error) {
        console.error("Erro ao carregar contatos para envio entre fluxos:", error);
        setMoveContatos([]);
      }
    } else {
      // Sem cliente o relacionamento fica vinculado ao card; oferecer o contato do atendimento
      setMoveContatos(
        contatoId
          ? [{ id: Number(contatoId), nome: sourceCard?.contact?.name || "Contato do atendimento" }]
          : []
      );
    }

    resetCloneModalState();
    setMoveReasonModalOpen(true);
  };

  const getFieldOptions = (field) => {
    if (!field?.fieldOptions) return [];
    if (Array.isArray(field.fieldOptions)) return field.fieldOptions;
    if (typeof field.fieldOptions === "string") {
      try {
        const parsed = JSON.parse(field.fieldOptions);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }
    if (typeof field.fieldOptions === "object") {
      return Object.values(field.fieldOptions);
    }
    return [];
  };

  const handleCardMove = async (cardId, fromColumnId, toColumnId, newOrder) => {
    if (fromColumnId !== toColumnId) {
      const movedCard = currentBoard?.columns
        ?.flatMap((column) => column.cards || [])
        ?.find((card) => Number(card.id) === Number(cardId));

      if (!movedCard) {
        toast.error("Nao foi possivel localizar os dados do card para validar a movimentacao");
        return;
      }

      const movePayload = { cardId, fromColumnId, toColumnId, newOrder };
      const targetColumn = currentBoard?.columns?.find((column) => Number(column.id) === Number(toColumnId));
      const requiredFieldNames = Array.isArray(targetColumn?.requiredFieldNames)
        ? targetColumn.requiredFieldNames
        : [];

      if (requiredFieldNames.length > 0) {
        const boardFields = normalizedBoardFields;

        const missingFields = requiredFieldNames
          .map((requiredKey) => {
            const resolvedDefinition = resolveBoardFieldDefinition(requiredKey, boardFields);
            const definition = resolvedDefinition || {
              fieldName: requiredKey,
              fieldLabel: requiredKey,
              fieldType: "text",
            };

            const resolvedFieldName = resolvedDefinition?.fieldName || requiredKey;

            const currentValue = getCardFieldValue(movedCard, resolvedFieldName);
            if (isMissingRequiredFieldValue(currentValue)) {
              return {
                ...definition,
                fieldName: resolvedFieldName,
                originalRequiredKey: requiredKey,
              };
            }
            return null;
          })
          .filter(Boolean);

        if (missingFields.length > 0) {
          const initialValues = {};
          missingFields.forEach((field) => {
            const currentValue = getCardFieldValue(movedCard, field.fieldName);
            if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
              initialValues[field.fieldName] = Array.isArray(currentValue) ? currentValue : [];
            } else {
              initialValues[field.fieldName] = currentValue ?? "";
            }
          });

          setPendingMove(movePayload);
          setPendingMoveCard(movedCard);
          setRequiredFieldsMode("move");
          setRequiredFieldsToFill(missingFields);
          setRequiredFieldsValues(initialValues);
          setRequiredFieldsErrors({});
          setRequiredFieldsModalOpen(true);
          return;
        }
      }

      await startPostValidationMoveFlow(movedCard, movePayload);
      return;
    }

    await executeCardMove(cardId, fromColumnId, toColumnId, newOrder, null, null, null);
  };

  // Reordenação dentro da mesma coluna (modo manual). Espelha handleColumnDrop:
  // atualização otimista + renormalização da ordem (0..n-1), com self-heal no erro.
  const handleCardReorder = async (columnId, orderedCardIds) => {
    if (!currentBoard) return;

    setCurrentBoard((prev) => ({
      ...prev,
      columns: prev.columns.map((col) => {
        if (col.id !== columnId) return col;
        const byId = new Map((col.cards || []).map((c) => [c.id, c]));
        const newCards = orderedCardIds
          .map((id, index) => {
            const c = byId.get(id);
            // Mantém o mesmo objeto quando a ordem não muda: o card não redesenha.
            if (!c) return null;
            return c.order === index ? c : { ...c, order: index };
          })
          .filter(Boolean);
        return { ...col, cards: newCards };
      }),
    }));

    try {
      await reorderCards(
        columnId,
        orderedCardIds.map((id, index) => ({ id, order: index }))
      );
    } catch (err) {
      console.error(err);
      toast.error("Erro ao reordenar cards");
      reloadCurrentBoard();
    }
  };

  const handleRequiredFieldChange = (fieldName, value) => {
    setRequiredFieldsValues((prev) => {
      const next = { ...prev, [fieldName]: value };

      // Boards de locação: trocar o tipo recalcula a data de devolução a partir
      // da data de lançamento do card. Mesma regra do CardModal.
      if (fieldName === TIPO_LOCACAO_FIELD) {
        const novaData = calcularDataDevolucao(value, pendingMoveCard?.createdAt);
        if (novaData) {
          next[DATA_DEVOLUCAO_FIELD] = novaData;
        }
      }

      return next;
    });

    setRequiredFieldsErrors((prev) => {
      const next = { ...prev, [fieldName]: false };
      if (fieldName === TIPO_LOCACAO_FIELD) {
        next[DATA_DEVOLUCAO_FIELD] = false;
      }
      return next;
    });
  };

  const handleRequiredFieldCheckboxChange = (fieldName, optionValue, checked) => {
    const currentValues = Array.isArray(requiredFieldsValues[fieldName])
      ? requiredFieldsValues[fieldName]
      : [];

    const nextValues = checked
      ? [...currentValues, optionValue]
      : currentValues.filter((value) => value !== optionValue);

    handleRequiredFieldChange(fieldName, nextValues);
  };

  const resetRequiredFieldsMoveState = () => {
    setRequiredFieldsModalOpen(false);
    setRequiredFieldsToFill([]);
    setRequiredFieldsValues({});
    setRequiredFieldsErrors({});
    setRequiredFieldsMode("move");
    setPendingMoveCard(null);
    setPendingClonePayload(null);
    setRequiredClienteSearch("");
    setRequiredContatoOptions([]);
  };

  const handleCancelRequiredFieldsMove = () => {
    const isCloneFlow = requiredFieldsMode === "clone";
    resetRequiredFieldsMoveState();
    setPendingMove(null);
    if (isCloneFlow) {
      toast.info("Envio cancelado");
      return;
    }

    toast.info("Movimentacao cancelada");
    reloadCurrentBoard();
  };

  const handleConfirmRequiredFieldsClone = async () => {
    if (!pendingClonePayload) {
      resetRequiredFieldsMoveState();
      return;
    }

    const errors = {};
    const missingLabels = [];

    requiredFieldsToFill.forEach((field) => {
      const value = requiredFieldsValues[field.fieldName];
      if (isMissingRequiredFieldValue(value)) {
        errors[field.fieldName] = true;
        missingLabels.push(field.fieldLabel || field.fieldName);
      }
    });

    if (missingLabels.length > 0) {
      setRequiredFieldsErrors(errors);
      toast.error(`Preencha os campos obrigatorios: ${missingLabels.join(", ")}`);
      return;
    }

    try {
      setSavingRequiredFields(true);

      const mergedPayload = {
        ...pendingClonePayload,
        customFields: {
          ...(pendingClonePayload.customFields || {}),
        },
      };

      const directFieldNames = new Set([
        "title",
        "description",
        "priority",
        "dueDate",
        "assignedUserId",
        "ticketId",
        "contactId",
        "clienteId",
      ]);

      requiredFieldsToFill.forEach((field) => {
        const rawValue = requiredFieldsValues[field.fieldName];
        mergedPayload.customFields[field.fieldName] = rawValue;

        if (
          field.originalRequiredKey &&
          String(field.originalRequiredKey) !== String(field.fieldName)
        ) {
          mergedPayload.customFields[field.originalRequiredKey] = rawValue;
        }

        if (directFieldNames.has(field.fieldName)) {
          if (["assignedUserId", "ticketId", "contactId", "clienteId"].includes(field.fieldName)) {
            mergedPayload[field.fieldName] = rawValue ? Number(rawValue) : null;
          } else {
            mergedPayload[field.fieldName] = rawValue;
          }
        }
      });

      const clonePayloadWithRequiredFields = {
        ...mergedPayload,
        transferMode: pendingClonePayload?.transferMode || "copy",
      };
      const sourceCloneCard = pendingMoveCard || pendingCloneCard;
      resetRequiredFieldsMoveState();
      await startPostValidationCloneFlow(
        clonePayloadWithRequiredFields,
        sourceCloneCard,
        clonePayloadWithRequiredFields.transferMode
      );
    } catch (error) {
      console.error("Erro ao concluir envio com campos obrigatorios:", error);
      toast.error(error.response?.data?.message || "Erro ao enviar card");
    } finally {
      setSavingRequiredFields(false);
    }
  };

  const handleConfirmRequiredFieldsMove = async () => {
    if (requiredFieldsMode === "clone") {
      await handleConfirmRequiredFieldsClone();
      return;
    }

    if (!pendingMove || !pendingMoveCard) {
      resetRequiredFieldsMoveState();
      return;
    }

    const errors = {};
    const missingLabels = [];

    requiredFieldsToFill.forEach((field) => {
      const value = requiredFieldsValues[field.fieldName];
      if (isMissingRequiredFieldValue(value)) {
        errors[field.fieldName] = true;
        missingLabels.push(field.fieldLabel || field.fieldName);
      }
    });

    if (missingLabels.length > 0) {
      setRequiredFieldsErrors(errors);
      toast.error(`Preencha os campos obrigatorios: ${missingLabels.join(", ")}`);
      return;
    }

    try {
      setSavingRequiredFields(true);

      const mergedCustomFields = {
        ...(pendingMoveCard.customFields || {}),
      };
      const directUpdatePayload = {};
      const directFieldNames = new Set([
        "title",
        "description",
        "priority",
        "dueDate",
        "assignedUserId",
        "ticketId",
        "contactId",
        "clienteId",
      ]);

      requiredFieldsToFill.forEach((field) => {
        const rawValue = requiredFieldsValues[field.fieldName];
        mergedCustomFields[field.fieldName] = rawValue;

        if (
          field.originalRequiredKey &&
          String(field.originalRequiredKey) !== String(field.fieldName)
        ) {
          mergedCustomFields[field.originalRequiredKey] = rawValue;
        }

        if (directFieldNames.has(field.fieldName)) {
          if (["assignedUserId", "ticketId", "contactId", "clienteId"].includes(field.fieldName)) {
            directUpdatePayload[field.fieldName] = rawValue ? Number(rawValue) : null;
          } else {
            directUpdatePayload[field.fieldName] = rawValue;
          }
        }
      });

      await updateCard(pendingMove.cardId, {
        ...directUpdatePayload,
        customFields: mergedCustomFields,
      });

      const updatedMovedCard = {
        ...pendingMoveCard,
        customFields: mergedCustomFields,
        ...directUpdatePayload,
      };

      resetRequiredFieldsMoveState();
      await startPostValidationMoveFlow(updatedMovedCard, pendingMove);
    } catch (error) {
      console.error("Erro ao salvar campos obrigatorios antes da movimentacao:", error);
      toast.error(error.response?.data?.message || "Erro ao salvar campos obrigatorios");
    } finally {
      setSavingRequiredFields(false);
    }
  };

  const executeCardMove = async (
    cardId,
    fromColumnId,
    toColumnId,
    newOrder,
    scheduleDate,
    scheduleDescription,
    moveReason,
    generateRelationship,
    relationshipData
  ) => {
    try {
      await moveCard(cardId, {
        fromColumnId,
        toColumnId,
        newOrder,
        ...(scheduleDate && {
          scheduleDate,
          scheduleDescription
        }),
        ...(moveReason && {
          moveReason
        }),
        ...(typeof generateRelationship === "boolean" && {
          generateRelationship
        }),
        ...(relationshipData && {
          relationshipData
        })
      });
      toast.success("Card movido com sucesso!");
      reloadCurrentBoard();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Erro ao mover card");
    }
  };

  const handleOpenMoveContatoForm = () => {
    setMoveContatoFormData({
      nome: "", cargo: "", telefone: "", ddd_telefone: "", celular: "",
      ddd_celular: "", email: "", email_secundario: "", whatsapp: "",
      linkedin: "", observacoes: "", principal: false, ativo: true,
    });
    setSavingMoveContato(false);
    setMoveContatoFormOpen(true);
  };

  const handleCloseMoveContatoForm = () => {
    if (savingMoveContato) return;
    setMoveContatoFormOpen(false);
  };

  const handleSaveMoveContato = async () => {
    if (savingMoveContato) return;
    if (!moveContatoFormData.nome || !moveContatoFormData.nome.trim()) {
      toast.error("Nome do contato é obrigatório");
      return;
    }
    if (!moveCardClienteId) {
      toast.error("Card sem cliente vinculado — não é possível criar contato");
      return;
    }
    setSavingMoveContato(true);
    try {
      const { data: novoContato } = await api.post(
        `/clientes/${moveCardClienteId}/contatos`,
        moveContatoFormData
      );
      toast.success("Contato criado com sucesso!");
      // Reload contacts list and auto-select the new one
      const { data } = await api.get(`/clientes/${moveCardClienteId}/contatos`);
      const contatosData = Array.isArray(data) ? data : (data.contatos || []);
      setMoveContatos(contatosData);
      if (novoContato?.id) {
        setMoveRelacionamentoData((prev) => ({ ...prev, contatoId: String(novoContato.id) }));
      }
      setMoveContatoFormOpen(false);
    } catch (error) {
      console.error("Erro ao criar contato:", error);
      toast.error(error.response?.data?.error || "Erro ao criar contato");
    } finally {
      setSavingMoveContato(false);
    }
  };

  const handleConfirmScheduleMove = () => {
    if (!moveScheduleDate) {
      toast.error("Data do agendamento é obrigatória");
      return;
    }
    if (!moveScheduleDescription || moveScheduleDescription.trim() === "") {
      toast.error("Observação do agendamento é obrigatória");
      return;
    }

    setScheduleMoveModalOpen(false);
    setMoveReasonModalMode("move");
    setMoveReasonModalOpen(true);
  };

  const handleMoveReasonChange = (event) => {
    setMoveReason(normalizeSpreadsheetText(event.target.value));
  };

  const handleMoveReasonPaste = (event) => {
    const pastedText = event.clipboardData?.getData("text/plain");
    if (!pastedText) {
      return;
    }

    event.preventDefault();

    const normalizedPastedText = normalizeSpreadsheetText(pastedText);
    const target = event.target;

    if (
      target &&
      typeof target.selectionStart === "number" &&
      typeof target.selectionEnd === "number"
    ) {
      const nextValue = insertTextAtSelection(
        moveReason,
        normalizedPastedText,
        target.selectionStart,
        target.selectionEnd
      );
      setMoveReason(nextValue);
      return;
    }

    setMoveReason((previousValue) => `${previousValue}${normalizedPastedText}`);
  };

  const handleConfirmMoveWithReason = async () => {
    const normalizedMoveReason = normalizeSpreadsheetText(moveReason);

    if (!normalizedMoveReason || normalizedMoveReason.trim() === "") {
      toast.error("O motivo da movimentação é obrigatório");
      return;
    }

    if (generateRelationshipOnMove) {
      // Sem cliente o backend registra o relacionamento vinculado ao card/atendimento
      if (!moveRelacionamentoData.meio_contato) {
        toast.error("Meio de Contato é obrigatório");
        return;
      }

      if (
        moveRegistrarFollowup &&
        (!moveFollowupData.assunto || !moveFollowupData.data_followup || !moveFollowupData.hora_followup)
      ) {
        toast.error("Para registrar follow-up, preencha assunto, data e hora");
        return;
      }
    }

    setMoveReasonModalOpen(false);

    const relationshipData = generateRelationshipOnMove
      ? {
          data_contato: moveRelacionamentoData.data_contato,
          hora_contato: moveRelacionamentoData.hora_contato,
          meio_contato: moveRelacionamentoData.meio_contato,
          contatoId: moveRelacionamentoData.contatoId || null,
          followup:
            moveRegistrarFollowup
              ? {
                  assunto: moveFollowupData.assunto,
                  descricao: moveReason || undefined,
                  data_followup: moveFollowupData.data_followup,
                  hora_followup: moveFollowupData.hora_followup,
                  status: moveFollowupData.status || "pendente",
                  contatoId: moveRelacionamentoData.contatoId || null,
                }
              : null,
        }
      : null;

    if (moveReasonModalMode === "clone") {
      if (!pendingClonePayload) {
        toast.error("Payload do envio não encontrado");
        return;
      }

      try {
        await createCard({
          ...pendingClonePayload,
          moveReason: normalizedMoveReason,
          generateRelationship: generateRelationshipOnMove,
          ...(relationshipData && { relationshipData }),
        });

        if (pendingClonePayload.transferMode === "move" && pendingClonePayload.sourceCardId) {
          await deleteCard(pendingClonePayload.sourceCardId);
        }

        toast.success(
          pendingClonePayload.transferMode === "move"
            ? "Card movido com sucesso!"
            : "Card copiado com sucesso!"
        );
        setPendingClonePayload(null);
        await reloadCurrentBoard();
      } catch (error) {
        console.error("Erro ao confirmar envio entre fluxos:", error);
        toast.error(error.response?.data?.message || "Erro ao enviar card para outro board");
      }

      return;
    }

    executeCardMove(
      pendingMove.cardId,
      pendingMove.fromColumnId,
      pendingMove.toColumnId,
      pendingMove.newOrder,
      moveScheduleDate || null,
      moveScheduleDescription || null,
      normalizedMoveReason,
      generateRelationshipOnMove,
      relationshipData
    );

    setMoveScheduleDate("");
    setMoveScheduleDescription("");
    setPendingMove(null);
  };

  const handleCancelMoveWithReason = () => {
    setMoveReasonModalOpen(false);
    setMoveReason("");

    if (moveReasonModalMode === "clone") {
      setPendingClonePayload(null);
      toast.info("Envio cancelado");
      return;
    }

    setPendingMove(null);
    setMoveScheduleDate("");
    setMoveScheduleDescription("");
    toast.info("Movimentação cancelada");
    // Recarregar board para restaurar estado original
    reloadCurrentBoard();
  };

  const handleCancelScheduleMove = () => {
    setScheduleMoveModalOpen(false);
    setPendingMove(null);
    setMoveScheduleDate("");
    setMoveScheduleDescription("");
    toast.info("Movimentação cancelada");
    // Recarregar board para restaurar estado original
    reloadCurrentBoard();
  };

  const handleRefresh = () => {
    if (currentBoard) {
      loadBoard(currentBoard.id);
    } else {
      loadBoards();
    }
  };

  // -------------------------------------------------------------------------
  // Predicados de filtro. Cada um devolve a lista intacta quando sua dimensao
  // esta vazia, de modo que `filterCards` compoe um AND apenas entre os filtros
  // realmente ativos. Dentro de um mesmo campo a semantica e OR.
  // -------------------------------------------------------------------------

  const filterCardsByCreatedAt = (cards) => {
    if (!isDateRangeActive(filters.createdAt)) return cards;
    const { start, end } = filters.createdAt;
    return cards.filter((card) => matchesDateRange(card.createdAt, start, end));
  };

  const filterCardsByDueDate = (cards) => {
    if (!isDateRangeActive(filters.dueDate)) return cards;
    const { start, end } = filters.dueDate;
    return cards.filter((card) => matchesDateRange(card.dueDate, start, end));
  };

  const filterCardsByTasks = (cards) => {
    const { type, text } = filters.tasks;
    if (type === "all" && !text.trim()) return cards;

    let filteredCards = cards;

    switch (type) {
      case "with-tasks":
        filteredCards = filteredCards.filter((card) => card.tasks && card.tasks.length > 0);
        break;

      case "without-tasks":
        filteredCards = filteredCards.filter((card) => !card.tasks || card.tasks.length === 0);
        break;

      case "with-pending":
        filteredCards = filteredCards.filter((card) =>
          card.tasks?.some((task) => !task.completed)
        );
        break;

      case "with-completed":
        filteredCards = filteredCards.filter((card) =>
          card.tasks?.some((task) => task.completed)
        );
        break;

      case "all-completed":
        filteredCards = filteredCards.filter(
          (card) => card.tasks?.length > 0 && card.tasks.every((task) => task.completed)
        );
        break;

      default:
        break;
    }

    if (text.trim()) {
      const searchTerm = text.toLowerCase().trim();
      filteredCards = filteredCards.filter((card) =>
        card.tasks?.some((task) => task.text && task.text.toLowerCase().includes(searchTerm))
      );
    }

    return filteredCards;
  };

  // OR: o card passa se tiver QUALQUER uma das tags marcadas.
  const filterCardsByTags = (cards) => {
    if (!filters.tags.length) return cards;
    const wanted = filters.tags.map(normalizeSearchText);

    return cards.filter((card) => {
      if (!Array.isArray(card.tags)) return false;
      const cardTags = card.tags.map((tag) => normalizeSearchText(normalizeTagValue(tag)));
      return wanted.some((tag) => cardTags.includes(tag));
    });
  };

  // N campos customizados ao mesmo tempo: AND entre campos, OR dentro do campo.
  // A comparacao depende do tipo declarado no fieldDefinition.
  const filterCardsByCustomFields = (cards) => {
    const activeFields = Object.entries(filters.customFields).filter(
      ([, values]) => values && values.length
    );
    if (!activeFields.length) return cards;

    const definitions = currentBoard?.fieldDefinitions || [];

    return cards.filter((card) => {
      const customFields = getCardCustomFields(card);

      return activeFields.every(([fieldName, selected]) => {
        const definition = definitions.find((f) => f.fieldName === fieldName);
        const fieldType = normalizeFieldType(definition?.fieldType);
        const cardValue = customFields[fieldName];

        if (cardValue === undefined || cardValue === null || cardValue === "") return false;

        switch (fieldType) {
          case "select":
          case "radio":
            return selected.some((value) => String(cardValue) === String(value));

          case "multiselect":
          case "checkbox": {
            // O valor gravado no card e um array: interseccao nao-vazia.
            const cardValues = (Array.isArray(cardValue) ? cardValue : [cardValue]).map(String);
            return selected.some((value) => cardValues.includes(String(value)));
          }

          case "number":
          case "money": {
            const [min, max] = selected;
            const numericValue = parseFloat(String(cardValue).replace(",", "."));
            if (Number.isNaN(numericValue)) return false;
            if (min && numericValue < parseFloat(String(min).replace(",", "."))) return false;
            if (max && numericValue > parseFloat(String(max).replace(",", "."))) return false;
            return true;
          }

          case "date": {
            const [start, end] = selected;
            return matchesDateRange(cardValue, start, end);
          }

          default: {
            const haystack = normalizeSearchText(cardValue);
            return selected.some((value) => haystack.includes(normalizeSearchText(value)));
          }
        }
      });
    });
  };

  // Filtro geral de texto (board-wide): busca em título, descrição, cliente vinculado
  // e campos personalizados — ignorando acentos/caixa.
  const filterCardsByText = (cards) => {
    const searchTerm = normalizeSearchText(debouncedCardFilterText);
    if (!searchTerm) return cards;

    const matches = (value) => normalizeSearchText(value).includes(searchTerm);
    const matchesCGC = (cnpj) => {
      const strip = (s) => String(s || "").replace(/[.\-/\s]/g, "");
      return strip(normalizeSearchText(cnpj)).includes(strip(searchTerm));
    };

    return cards.filter((card) => {
      if (matches(card.title)) return true;
      if (matches(card.description)) return true;
      if (
        matches(card.cliente?.NOMECLI) ||
        matches(card.cliente?.NOMEFANTASIA) ||
        matchesCGC(card.cliente?.CGCCLI)
      ) {
        return true;
      }
      const customFields = getCardCustomFields(card);
      if (Object.keys(customFields).length > 0) {
        return Object.values(customFields).some((value) => {
          if (typeof value === "string") return matches(value);
          if (Array.isArray(value)) {
            return value.some((v) => typeof v === "string" && matches(v));
          }
          return false;
        });
      }
      return false;
    });
  };

  // AND entre todas as dimensoes ativas.
  const filterCards = (cards) => {
    if (!cards) return [];

    let result = cards;
    result = filterCardsByCreatedAt(result);
    result = filterCardsByDueDate(result);
    result = filterCardsByTasks(result);
    result = filterCardsByTags(result);
    result = filterCardsByCustomFields(result);
    result = filterCardsByText(result);
    return result;
  };

  // Filtragem memoizada: antes rodava uma vez por coluna aqui e outra dentro do
  // contador, a cada render — mesmo sem os filtros terem mudado.
  const boardWithFilteredCards = useMemo(() => {
    if (!currentBoard) return null;
    return {
      ...currentBoard,
      // Ordena aqui (copia) em vez de no JSX: o array agora e memoizado e um
      // .sort() durante o render mutaria o valor memoizado.
      columns: [...(currentBoard.columns || [])]
        .sort((a, b) => a.order - b.order)
        .map((column) => ({
          ...column,
          cards: filterCards(column.cards),
        })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBoard, filters, debouncedCardFilterText]);

  const filteredCardsCount = useMemo(
    () =>
      boardWithFilteredCards?.columns?.reduce(
        (total, column) => total + (column.cards?.length || 0),
        0
      ) || 0,
    [boardWithFilteredCards]
  );

  const totalCardsCount = useMemo(
    () =>
      currentBoard?.columns?.reduce((total, column) => total + (column.cards?.length || 0), 0) || 0,
    [currentBoard]
  );

  // Quantas dimensoes estao ativas (alimenta o badge do botao "Filtros").
  // A busca geral de texto nao entra: ela mora fora do Popover.
  const activeFilterCount = useMemo(() => {
    let count = Object.keys(filters.customFields).length;
    if (isDateRangeActive(filters.createdAt)) count += 1;
    if (isDateRangeActive(filters.dueDate)) count += 1;
    if (filters.tasks.type !== "all" || filters.tasks.text.trim()) count += 1;
    if (filters.tags.length) count += 1;
    return count;
  }, [filters]);

  const filtersActive = activeFilterCount > 0 || Boolean(debouncedCardFilterText.trim());

  // Opcoes de tag derivadas dos cards carregados: nenhuma tela do Kanban grava
  // tags hoje, entao a seccao so aparece se o board de fato tiver alguma.
  const tagOptions = useMemo(() => {
    const found = new Set();
    currentBoard?.columns?.forEach((column) =>
      column.cards?.forEach((card) => {
        if (!Array.isArray(card.tags)) return;
        card.tags.forEach((tag) => {
          const value = normalizeTagValue(tag);
          if (value) found.add(value);
        });
      })
    );
    return Array.from(found).sort();
  }, [currentBoard]);

  const boardFilterFields = useMemo(() => {
    return (currentBoard?.fieldDefinitions || []).filter(
      (field) => !isDedicatedFilterField(field.fieldName)
    );
  }, [currentBoard]);

  const setCustomFieldFilter = (fieldName, values) => {
    setFilters((prev) => {
      const customFields = { ...prev.customFields };
      const hasValue = values && values.length && values.some((value) => value !== "" && value !== null);
      if (hasValue) customFields[fieldName] = values;
      else delete customFields[fieldName];
      return { ...prev, customFields };
    });
  };

  const setDateRangeFilter = (key, part, value) => {
    setFilters((prev) => ({ ...prev, [key]: { ...prev[key], [part]: value } }));
  };

  const clearAllFilters = () => {
    setFilters(createEmptyFilters());
    setCardFilterText("");
    setDebouncedCardFilterText("");
  };

  // Um controle por fieldDefinition, escolhido pelo tipo do campo. Campos de
  // opcoes viram multi-select (OR); number/money e date viram intervalo.
  const renderCustomFieldFilter = (field) => {
    const fieldType = normalizeFieldType(field.fieldType);
    const selected = filters.customFields[field.fieldName] || [];

    if (["select", "radio", "multiselect", "checkbox"].includes(fieldType)) {
      const options = (field.fieldOptions || []).map(getFieldOptionValue);
      const labelByValue = new Map(
        (field.fieldOptions || []).map((option) => [
          getFieldOptionValue(option),
          getFieldOptionLabel(option),
        ])
      );

      return (
        <Autocomplete
          multiple
          size="small"
          options={options}
          value={selected}
          getOptionLabel={(option) => labelByValue.get(option) || option}
          onChange={(_event, value) => setCustomFieldFilter(field.fieldName, value)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                size="small"
                label={labelByValue.get(option) || option}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label={field.fieldLabel} />
          )}
        />
      );
    }

    if (fieldType === "number" || fieldType === "money") {
      const [min = "", max = ""] = selected;
      return (
        <>
          <Typography variant="subtitle2" className={classes.filterSectionTitle}>
            {field.fieldLabel}
          </Typography>
          <div className={classes.filterDateRange}>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              label="Mín."
              value={min}
              onChange={(e) => setCustomFieldFilter(field.fieldName, [e.target.value, max])}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              variant="outlined"
              size="small"
              type="number"
              label="Máx."
              value={max}
              onChange={(e) => setCustomFieldFilter(field.fieldName, [min, e.target.value])}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>
        </>
      );
    }

    if (fieldType === "date") {
      const [start = "", end = ""] = selected;
      return (
        <>
          <Typography variant="subtitle2" className={classes.filterSectionTitle}>
            {field.fieldLabel}
          </Typography>
          <div className={classes.filterDateRange}>
            <TextField
              variant="outlined"
              size="small"
              type="date"
              label="De"
              value={start}
              onChange={(e) => setCustomFieldFilter(field.fieldName, [e.target.value, end])}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              variant="outlined"
              size="small"
              type="date"
              label="Até"
              value={end}
              onChange={(e) => setCustomFieldFilter(field.fieldName, [start, e.target.value])}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </div>
        </>
      );
    }

    return (
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        label={field.fieldLabel}
        placeholder={`Buscar por ${field.fieldLabel}...`}
        value={selected[0] || ""}
        onChange={(e) => setCustomFieldFilter(field.fieldName, e.target.value ? [e.target.value] : [])}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" className={classes.filterSearchIcon} />
            </InputAdornment>
          ),
        }}
      />
    );
  };

  // Um chip por dimensao ativa; remover um chip nao mexe nos demais.
  const buildActiveFilterChips = () => {
    const chips = [];

    if (isDateRangeActive(filters.createdAt)) {
      chips.push({
        key: "createdAt",
        label: `Lançamento: ${formatDateRangeLabel(filters.createdAt)}`,
        onDelete: () => setFilters((prev) => ({ ...prev, createdAt: { start: "", end: "" } })),
      });
    }

    if (isDateRangeActive(filters.dueDate)) {
      chips.push({
        key: "dueDate",
        label: `Vencimento: ${formatDateRangeLabel(filters.dueDate)}`,
        onDelete: () => setFilters((prev) => ({ ...prev, dueDate: { start: "", end: "" } })),
      });
    }

    if (filters.tasks.type !== "all" || filters.tasks.text.trim()) {
      const parts = [];
      if (filters.tasks.type !== "all") parts.push(TASK_FILTER_LABELS[filters.tasks.type]);
      if (filters.tasks.text.trim()) parts.push(`"${filters.tasks.text.trim()}"`);
      chips.push({
        key: "tasks",
        label: `Tarefas: ${parts.join(" · ")}`,
        onDelete: () => setFilters((prev) => ({ ...prev, tasks: { type: "all", text: "" } })),
      });
    }

    if (filters.tags.length) {
      chips.push({
        key: "tags",
        label: `Tags: ${filters.tags.join(", ")}`,
        onDelete: () => setFilters((prev) => ({ ...prev, tags: [] })),
      });
    }

    Object.entries(filters.customFields).forEach(([fieldName, values]) => {
      const field = currentBoard?.fieldDefinitions?.find((f) => f.fieldName === fieldName);
      const fieldType = normalizeFieldType(field?.fieldType);
      const label = field?.fieldLabel || fieldName;

      let valueLabel;
      if (fieldType === "number" || fieldType === "money") {
        const [min, max] = values;
        valueLabel = min && max ? `${min} – ${max}` : min ? `≥ ${min}` : `≤ ${max}`;
      } else if (fieldType === "date") {
        valueLabel = formatDateRangeLabel({ start: values[0], end: values[1] });
      } else {
        valueLabel = values.join(", ");
      }

      chips.push({
        key: `custom:${fieldName}`,
        label: `${label}: ${valueLabel}`,
        onDelete: () => setCustomFieldFilter(fieldName, []),
      });
    });

    return chips;
  };

  // Tipo de locacao valendo no modal: o que o usuario acabou de escolher aqui
  // ou, se o campo nao estiver entre os obrigatorios, o que ja esta no card.
  const getTipoLocacaoEfetivo = () => {
    const doModal = requiredFieldsValues[TIPO_LOCACAO_FIELD];
    if (doModal !== undefined && doModal !== null && String(doModal).trim() !== "") {
      return doModal;
    }
    return getCardFieldValue(pendingMoveCard, TIPO_LOCACAO_FIELD);
  };

  const renderRequiredMoveField = (field) => {
    const value = requiredFieldsValues[field.fieldName];
    const hasError = Boolean(requiredFieldsErrors[field.fieldName]);
    const label = `${field.fieldLabel || field.fieldName} *`;
    const fieldType = normalizeFieldType(field.fieldType);

    // Campos com fonte de dados externa entram antes do tipo: continuam "text"
    // no banco, mas a entrada e um autocomplete.
    if (isImobilizadoField(field)) {
      return (
        <ImobilizadoAutocomplete
          key={field.fieldName}
          label={label}
          value={value || ""}
          onChange={(novoValor) => handleRequiredFieldChange(field.fieldName, novoValor)}
          error={hasError}
          helperText={hasError ? "Este campo e obrigatorio" : undefined}
          placeholder={field.placeholder}
        />
      );
    }

    if (field.fieldName === "clienteId") {
      const selectedCliente = requiredClienteOptions.find(
        (cliente) => Number(cliente.CODCLI) === Number(value)
      ) || null;

      return (
        <Autocomplete
          key={field.fieldName}
          options={requiredClienteOptions}
          loading={loadingRequiredClientes}
          value={selectedCliente}
          getOptionLabel={(option) => `${option.NOMECLI || "Cliente"} (#${option.CODCLI})`}
          onChange={(_, option) => handleRequiredFieldChange(field.fieldName, option ? option.CODCLI : "")}
          onInputChange={(_, inputValue) => setRequiredClienteSearch(inputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              margin="dense"
              error={hasError}
              helperText={hasError ? "Este campo e obrigatorio" : undefined}
            />
          )}
        />
      );
    }

    if (field.fieldName === "assignedUserId") {
      const selectedUser = users.find((userItem) => Number(userItem.id) === Number(value)) || null;

      return (
        <Autocomplete
          key={field.fieldName}
          options={users}
          value={selectedUser}
          loading={loadingUsers}
          getOptionLabel={(option) => option?.name || ""}
          onChange={(_, option) => handleRequiredFieldChange(field.fieldName, option ? option.id : "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              margin="dense"
              error={hasError}
              helperText={hasError ? "Este campo e obrigatorio" : undefined}
            />
          )}
        />
      );
    }

    if (field.fieldName === "contatoId") {
      const selectedContato = requiredContatoOptions.find(
        (contato) => Number(contato.id) === Number(value)
      ) || null;
      const hasClienteSelected = Boolean(requiredFieldsValues.clienteId || pendingMoveCard?.clienteId);

      return (
        <Autocomplete
          key={field.fieldName}
          options={requiredContatoOptions}
          value={selectedContato}
          loading={loadingRequiredContatos}
          disabled={!hasClienteSelected}
          getOptionLabel={(option) => option?.nome || option?.NOME || `Contato #${option?.id || ""}`}
          onChange={(_, option) => handleRequiredFieldChange(field.fieldName, option ? option.id : "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              margin="dense"
              error={hasError}
              helperText={
                hasError
                  ? "Este campo e obrigatorio"
                  : hasClienteSelected
                    ? undefined
                    : "Selecione um cliente para carregar os contatos"
              }
            />
          )}
        />
      );
    }

    if (field.fieldName === "ticketId") {
      const selectedTicket = requiredTicketOptions.find(
        (ticket) => Number(ticket.id) === Number(value)
      ) || null;

      return (
        <Autocomplete
          key={field.fieldName}
          options={requiredTicketOptions}
          value={selectedTicket}
          getOptionLabel={(option) => option?.label || `#${option?.id || ""}`}
          onChange={(_, option) => handleRequiredFieldChange(field.fieldName, option ? option.id : "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              margin="dense"
              error={hasError}
              helperText={hasError ? "Este campo e obrigatorio" : undefined}
            />
          )}
        />
      );
    }

    if (fieldType === "textarea") {
      return (
        <TextField
          key={field.fieldName}
          label={label}
          value={value || ""}
          onChange={(e) => handleRequiredFieldChange(field.fieldName, e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          margin="dense"
          error={hasError}
          helperText={hasError ? "Este campo e obrigatorio" : undefined}
        />
      );
    }

    if (fieldType === "select") {
      const options = getFieldOptions(field);
      return (
        <FormControl key={field.fieldName} variant="outlined" fullWidth margin="dense" error={hasError}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value || ""}
            onChange={(e) => handleRequiredFieldChange(field.fieldName, e.target.value)}
            label={label}
          >
            <MenuItem value="">
              <em>Selecione</em>
            </MenuItem>
            {options.map((option, index) => (
              <MenuItem key={option.value || index} value={option.value || option.label || option}>
                {option.label || option.value || option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (fieldType === "radio") {
      const options = getFieldOptions(field);
      return (
        <FormControl key={field.fieldName} component="fieldset" fullWidth margin="dense" error={hasError}>
          <FormLabel component="legend">{label}</FormLabel>
          <RadioGroup
            value={value || ""}
            onChange={(e) => handleRequiredFieldChange(field.fieldName, e.target.value)}
          >
            {options.map((option, index) => {
              const optionValue = option.value || option.label || option;
              const optionLabel = option.label || option.value || option;
              return (
                <FormControlLabel
                  key={optionValue || index}
                  value={optionValue}
                  control={<Radio color="primary" />}
                  label={optionLabel}
                />
              );
            })}
          </RadioGroup>
          {hasError && <FormHelperText>Este campo e obrigatorio</FormHelperText>}
        </FormControl>
      );
    }

    if (fieldType === "multiselect") {
      const options = getFieldOptions(field);
      const selectedValues = Array.isArray(value) ? value : [];

      return (
        <FormControl key={field.fieldName} variant="outlined" fullWidth margin="dense" error={hasError}>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={selectedValues}
            onChange={(e) => handleRequiredFieldChange(field.fieldName, e.target.value)}
            label={label}
            renderValue={(selected) =>
              selected
                .map((selectedValue) => {
                  const option = options.find(
                    (item) => String(item.value || item.label || item) === String(selectedValue)
                  );
                  return option?.label || option?.value || selectedValue;
                })
                .join(", ")
            }
          >
            {options.map((option, index) => {
              const optionValue = option.value || option.label || option;
              return (
                <MenuItem key={optionValue || index} value={optionValue}>
                  <Checkbox
                    checked={selectedValues.includes(optionValue)}
                    onChange={(e) =>
                      handleRequiredFieldCheckboxChange(field.fieldName, optionValue, e.target.checked)
                    }
                  />
                  <Typography>{option.label || option.value || option}</Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    }

    if (fieldType === "checkbox") {
      const options = getFieldOptions(field);
      const selectedValues = Array.isArray(value) ? value : [];

      return (
        <FormControl key={field.fieldName} component="fieldset" fullWidth margin="dense" error={hasError}>
          <FormLabel component="legend">{label}</FormLabel>
          <Box style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
            {options.map((option, index) => {
              const optionValue = option.value || option.label || option;
              const optionLabel = option.label || option.value || option;
              return (
                <FormControlLabel
                  key={optionValue || index}
                  control={
                    <Checkbox
                      checked={selectedValues.includes(optionValue)}
                      onChange={(e) =>
                        handleRequiredFieldCheckboxChange(field.fieldName, optionValue, e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={optionLabel}
                />
              );
            })}
          </Box>
          {hasError && <FormHelperText>Este campo e obrigatorio</FormHelperText>}
        </FormControl>
      );
    }

    if (fieldType === "date") {
      // Mesma regra do CardModal: a data de devolucao e derivada do tipo de
      // locacao, entao so ESPECIAL libera a digitacao.
      const travadaPeloTipo =
        field.fieldName === DATA_DEVOLUCAO_FIELD &&
        !isDevolucaoEditavel(getTipoLocacaoEfetivo());

      return (
        <TextField
          key={field.fieldName}
          label={label}
          type="date"
          value={value || ""}
          onChange={(e) => handleRequiredFieldChange(field.fieldName, e.target.value)}
          variant="outlined"
          fullWidth
          margin="dense"
          disabled={travadaPeloTipo}
          error={hasError}
          helperText={
            hasError
              ? "Este campo e obrigatorio"
              : travadaPeloTipo
                ? "Calculada pelo tipo de locacao"
                : undefined
          }
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    if (fieldType === "color") {
      return (
        <FormControl key={field.fieldName} fullWidth margin="dense" error={hasError}>
          <FormLabel component="legend" style={{ marginBottom: 8 }}>{label}</FormLabel>
          <Box display="flex" alignItems="center" gap={12}>
            <input
              type="color"
              value={value || "#000000"}
              onChange={(e) => handleRequiredFieldChange(field.fieldName, e.target.value)}
              style={{ width: 48, height: 40, border: "1px solid #ccc", borderRadius: 4 }}
            />
            <Typography variant="body2">{value || "#000000"}</Typography>
          </Box>
          {hasError && <FormHelperText>Este campo e obrigatorio</FormHelperText>}
        </FormControl>
      );
    }

    if (fieldType === "money") {
      return (
        <FormControl key={field.fieldName} fullWidth margin="dense" error={hasError}>
          <MonetaryInput
            label={label}
            value={value || 0}
            onChange={(moneyValue) => handleRequiredFieldChange(field.fieldName, moneyValue)}
            variant="outlined"
            margin="dense"
            fullWidth
          />
          {hasError && <FormHelperText>Este campo e obrigatorio</FormHelperText>}
        </FormControl>
      );
    }

    const inputType = fieldType === "number" ? "number" : fieldType === "email" ? "email" : "text";

    return (
      <TextField
        key={field.fieldName}
        label={label}
        type={inputType}
        value={value || ""}
        onChange={(e) => handleRequiredFieldChange(field.fieldName, e.target.value)}
        variant="outlined"
        fullWidth
        margin="dense"
        error={hasError}
        helperText={hasError ? "Este campo e obrigatorio" : undefined}
      />
    );
  };

  // Props com identidade fixa para KanbanColumn/KanbanCard (React.memo). Antes
  // cada render da página criava funções novas e redesenhava todos os cards.
  // Ficam aqui, depois de todos os handlers e antes do return antecipado.
  const onCardEdit = useStableCallback(handleEditCard);
  const onCardDelete = useStableCallback(handleDeleteCard);
  const onCardClone = useStableCallback(handleOpenCloneCardModal);
  const onCardMove = useStableCallback(handleCardMove);
  const onCardReorder = useStableCallback(handleCardReorder);
  const onAddCard = useStableCallback(handleAddCard);
  const onEditColumn = useStableCallback(handleEditColumn);
  const onClearColumn = useStableCallback(handleClearColumn);
  const onDeleteColumn = useStableCallback(handleDeleteColumn);
  const onColumnDragStart = useStableCallback(handleColumnDragStart);
  const onColumnDragEnd = useStableCallback(handleColumnDragEnd);
  const onColumnDragOver = useStableCallback(handleColumnDragOver);
  const onColumnDrop = useStableCallback(handleColumnDrop);
  const onCardDragEnd = useCallback(() => setDraggingCard(null), []);

  // Card do deep link (?cardId=): a coluna dele se expande até mostrá-lo.
  const focusCardId = useMemo(
    () => Number(new URLSearchParams(location.search).get("cardId")) || null,
    [location.search]
  );

  const searchLower = boardSearchText.toLowerCase().trim();
  const boardIdsWithCardMatch = new Set(cardSearchResults.map(c => c.boardId));
  const orderedBoards = [...boards]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .filter(b =>
      !searchLower ||
      b.name?.toLowerCase().includes(searchLower) ||
      b.description?.toLowerCase().includes(searchLower) ||
      boardIdsWithCardMatch.has(b.id)
    );

  const isAdminOrSuper = Boolean(user?.super || user?.profile?.toLowerCase() === "admin");
  const currentPermissions = currentBoard?.userPermissions || {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  };

  const canCreateBoard = isAdminOrSuper;
  const canEditBoard = isAdminOrSuper || currentPermissions.canEdit;
  const canDeleteBoard = isAdminOrSuper || currentPermissions.canDelete;
  const canCreateCard = isAdminOrSuper || currentPermissions.canCreate;
  const canEditItems = isAdminOrSuper || currentPermissions.canEdit;
  const canDeleteItems = isAdminOrSuper || currentPermissions.canDelete;

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header} style={{ marginLeft: "15px" }}>
        <div className={classes.title}>
          {currentBoard && (
            <IconButton size="small" onClick={handleBackToBoards}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h5" className={classes.titleText}>
            {currentBoard ? currentBoard.name : "CRMs"}
          </Typography>
          {currentBoard && (canEditBoard || canDeleteBoard) && (
            <Tooltip title="Opções do board">
              <IconButton size="small" onClick={handleBoardMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {!currentBoard && (
          <TextField
            size="small"
            variant="outlined"
            placeholder="Pesquisar boards e cards..."
            value={boardSearchText}
            onChange={(e) => setBoardSearchText(e.target.value)}
            style={{ width: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: boardSearchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setBoardSearchText(""); setCardSearchResults([]); }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        )}

        <div className={classes.actions}>
          <Tooltip title="Atualizar">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {!currentBoard && canCreateBoard && (
            <>
              <Tooltip title="Criar a partir de template">
                <IconButton onClick={handleOpenTemplateMenu} disabled={seedingBoards}>
                  <LibraryAddIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={templateMenuAnchor}
                open={Boolean(templateMenuAnchor)}
                onClose={() => setTemplateMenuAnchor(null)}
              >
                {templates.length === 0 ? (
                  <MenuItem disabled>Nenhum template disponível</MenuItem>
                ) : (
                  templates.map((t) => (
                    <MenuItem key={t.name} onClick={() => handleSeedTemplate(t.name)}>
                      {t.name}
                    </MenuItem>
                  ))
                )}
              </Menu>
            </>
          )}

          {currentBoard ? (
            <>
              <Tooltip title="Imprimir relatório do board">
                <IconButton
                  onClick={() => setBoardReportOpen(true)}
                  size="small"
                  style={{ marginRight: 4 }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              {canEditItems && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ViewColumnIcon />}
                  onClick={handleAddColumn}
                >
                  Nova Coluna
                </Button>
              )}
            </>
          ) : (
            canCreateBoard && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddBoard}
              >
                Novo Board
              </Button>
            )
          )}
        </div>
      </div>

      {/* Barra de filtros: botão "Filtros" (Popover) + chips das dimensões ativas */}
      {currentBoard && (
        <div className={classes.filterContainer}>
          <div className={classes.filterActiveChips}>
            <Badge badgeContent={activeFilterCount} color="primary">
              <Button
                variant="outlined"
                size="medium"
                startIcon={<FilterListIcon />}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              >
                Filtros
              </Button>
            </Badge>

            {buildActiveFilterChips().map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                size="small"
                onDelete={chip.onDelete}
              />
            ))}

            {filtersActive && (
              <>
                <Chip
                  label={`${filteredCardsCount} de ${totalCardsCount} cards`}
                  color="primary"
                  size="small"
                />
                <Button size="small" color="primary" onClick={clearAllFilters}>
                  Limpar tudo
                </Button>
              </>
            )}
          </div>

          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            classes={{ paper: classes.filterPopoverPaper }}
          >
            {/* Datas */}
            <div className={classes.filterSection}>
              <Typography variant="subtitle2" className={classes.filterSectionTitle}>
                Data de Lançamento
              </Typography>
              <div className={classes.filterDateRange}>
                <TextField
                  variant="outlined"
                  size="small"
                  label="De"
                  type="date"
                  value={filters.createdAt.start}
                  onChange={(e) => setDateRangeFilter("createdAt", "start", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  variant="outlined"
                  size="small"
                  label="Até"
                  type="date"
                  value={filters.createdAt.end}
                  onChange={(e) => setDateRangeFilter("createdAt", "end", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </div>
            </div>

            <div className={classes.filterSection}>
              <Typography variant="subtitle2" className={classes.filterSectionTitle}>
                Data de Vencimento
              </Typography>
              <div className={classes.filterDateRange}>
                <TextField
                  variant="outlined"
                  size="small"
                  label="De"
                  type="date"
                  value={filters.dueDate.start}
                  onChange={(e) => setDateRangeFilter("dueDate", "start", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  variant="outlined"
                  size="small"
                  label="Até"
                  type="date"
                  value={filters.dueDate.end}
                  onChange={(e) => setDateRangeFilter("dueDate", "end", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </div>
            </div>

            <Divider />

            {/* Tarefas */}
            <div className={classes.filterSection} style={{ marginTop: 16 }}>
              <Typography variant="subtitle2" className={classes.filterSectionTitle}>
                Tarefas
              </Typography>
              <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 8 }}>
                <InputLabel id="task-filter-label">Tipo de Tarefa</InputLabel>
                <Select
                  labelId="task-filter-label"
                  value={filters.tasks.type}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, tasks: { ...prev.tasks, type: e.target.value } }))
                  }
                  label="Tipo de Tarefa"
                >
                  {Object.entries(TASK_FILTER_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {value === "all" ? null : <AssignmentIcon fontSize="small" />}
                        <span>{label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Buscar texto nas tarefas..."
                value={filters.tasks.text}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, tasks: { ...prev.tasks, text: e.target.value } }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" className={classes.filterSearchIcon} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            {/* Tags: só existe se algum card do board tiver tags */}
            {tagOptions.length > 0 && (
              <>
                <Divider />
                <div className={classes.filterSection} style={{ marginTop: 16 }}>
                  <Typography variant="subtitle2" className={classes.filterSectionTitle}>
                    Tags
                  </Typography>
                  <Autocomplete
                    multiple
                    size="small"
                    options={tagOptions}
                    value={filters.tags}
                    onChange={(_event, value) => setFilters((prev) => ({ ...prev, tags: value }))}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip size="small" label={option} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" placeholder="Selecione tags" />
                    )}
                  />
                </div>
              </>
            )}

            {/* Campos do board */}
            {boardFilterFields.length > 0 && (
              <>
                <Divider />
                <div style={{ marginTop: 16 }}>
                  <Typography variant="subtitle2" className={classes.filterSectionTitle}>
                    Campos do board
                  </Typography>
                  {boardFilterFields.map((field) => (
                    <div key={field.id} className={classes.filterSection}>
                      {renderCustomFieldFilter(field)}
                    </div>
                  ))}
                </div>
              </>
            )}

            <Divider />
            <Box display="flex" justifyContent="flex-end" style={{ marginTop: 12 }}>
              <Button size="small" onClick={clearAllFilters} disabled={!filtersActive}>
                Limpar tudo
              </Button>
            </Box>
          </Popover>

          {/* Filtro geral de cards + ordenação geral (board-wide), alinhados à direita */}
          <div className={classes.boardFilterRight}>
            <TextField
              className={classes.boardCardFilter}
              variant="outlined"
              size="small"
              placeholder="Filtrar cards..."
              value={cardFilterText}
              onChange={(e) => setCardFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" className={classes.filterSearchIcon} />
                  </InputAdornment>
                ),
                endAdornment: cardFilterText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setCardFilterText("")}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControl variant="outlined" size="small" className={classes.boardSortSelect}>
              <InputLabel id="board-sort-label">Ordenar por</InputLabel>
              <Select
                labelId="board-sort-label"
                value={boardSortOption}
                onChange={(e) => setBoardSortOption(e.target.value)}
                label="Ordenar por"
              >
                <MenuItem value="order">Ordem manual</MenuItem>
                <MenuItem value="date-desc">Data (mais recentes)</MenuItem>
                <MenuItem value="date-asc">Data (mais antigos)</MenuItem>
                <MenuItem value="alpha-asc">Ordem alfabética</MenuItem>
                <MenuItem value="ticket-id">Ticket ID</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      )}

      {/* Content */}
      {!currentBoard ? (
        // Boards List
        <div className={classes.boardsContainer}>
          {boards.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Nenhum board criado
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Crie seu primeiro board para começar a organizar suas tarefas
              </Typography>
              {canCreateBoard && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddBoard}
                >
                  Criar Primeiro Board
                </Button>
              )}
              {canCreateBoard && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={seedingBoards ? <CircularProgress size={16} color="inherit" /> : <ViewColumnIcon />}
                  onClick={handleSeedDefaultBoards}
                  disabled={seedingBoards}
                >
                  {seedingBoards ? "Criando boards padrão..." : "Criar Boards Padrão"}
                </Button>
              )}
            </div>
          ) : (
            <>
              {cardSearchResults.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <Typography variant="subtitle1" gutterBottom style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    Cards encontrados ({cardSearchResults.length})
                    {searchingCards && <CircularProgress size={14} />}
                  </Typography>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {cardSearchResults.map(card => (
                      <Paper
                        key={card.id}
                        elevation={1}
                        style={{ padding: "8px 12px", cursor: "pointer", minWidth: 200, maxWidth: 280 }}
                        onClick={() => {
                          const board = boards.find(b => b.id === card.boardId);
                          if (board) {
                            setPendingCardToOpen(card.id);
                            handleBoardClick(board);
                          }
                        }}
                      >
                        <Typography variant="body2" style={{ fontWeight: 600 }}>{card.title}</Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {card.board?.name} → {card.column?.name}
                        </Typography>
                        {card.contact?.name && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {card.contact.name}
                          </Typography>
                        )}
                        {card.assignedUser?.name && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            Resp: {card.assignedUser.name}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </div>
                </div>
              )}
              {searchingCards && cardSearchResults.length === 0 && (
                <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="textSecondary">Buscando cards...</Typography>
                </div>
              )}
            <div className={classes.boardsGrid}>
              {orderedBoards.map((board) => (
                <Paper
                  key={board.id}
                  className={classes.boardCard}
                  elevation={2}
                  onClick={() => handleBoardClick(board)}
                  draggable
                  onDragStart={(event) => handleBoardDragStart(board.id, event)}
                  onDragOver={handleBoardDragOver}
                  onDrop={(event) => handleBoardDrop(board.id, event)}
                  onDragEnd={handleBoardDragEnd}
                >
                  <div className={classes.boardCardActions}>
                    {(isAdminOrSuper || board.userPermissions?.canEdit) && (
                      <Tooltip title="Editar board">
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditBoard(board, e)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(isAdminOrSuper || board.userPermissions?.canDelete) && (
                      <Tooltip title="Excluir board">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteBoardClick(board, e)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                  <Typography variant="h6" gutterBottom>
                    {board.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {board.description || "Sem descrição"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 8, display: "block" }}
                  >
                    {board.columns?.length || 0} colunas •{" "}
                    {board.cardsCount ??
                      (board.columns?.reduce((total, col) => total + (col.cards?.length || 0), 0) || 0)} cards
                  </Typography>
                </Paper>
              ))}
            </div>
            </>
          )}
        </div>
      ) : (
        // Board View with Columns
        <div className={classes.columnsContainer} ref={columnsContainerRef}>
          {boardWithFilteredCards.columns?.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Nenhuma coluna criada
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Adicione colunas para organizar seus cards
              </Typography>
              {canEditItems && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ViewColumnIcon />}
                  onClick={handleAddColumn}
                  style={{ marginTop: 16 }}
                >
                  Criar Primeira Coluna
                </Button>
              )}
            </div>
          ) : (
            <>
              {filteredCardsCount === 0 && filtersActive ? (
                <div className={classes.emptyState}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Nenhum card encontrado
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Nenhum card corresponde aos filtros selecionados
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={clearAllFilters}
                    style={{ marginTop: 16 }}
                  >
                    Limpar Filtros
                  </Button>
                </div>
              ) : (
                // Cards do quadro inteiro com a altura do maior card visível
                <KanbanCardHeightProvider>
                {boardWithFilteredCards.columns
                  ?.map((column) => (
                    <KanbanColumn
                      key={column.id}
                      column={column}
                      boardId={currentBoard?.id}
                      sortOption={boardSortOption}
                      filtersActive={filtersActive}
                      onCardEdit={onCardEdit}
                      onCardDelete={onCardDelete}
                      onCardClone={onCardClone}
                      onCardMove={onCardMove}
                      onCardReorder={onCardReorder}
                      onAddCard={onAddCard}
                      onEditColumn={onEditColumn}
                      onClearColumn={onClearColumn}
                      onDeleteColumn={onDeleteColumn}
                      draggingCardId={draggingCard?.id ?? null}
                      focusCardId={focusCardId}
                      onDragStart={setDraggingCard}
                      onDragEnd={onCardDragEnd}
                      onColumnDragStart={onColumnDragStart}
                      onColumnDragEnd={onColumnDragEnd}
                      onColumnDragOver={onColumnDragOver}
                      onColumnDrop={onColumnDrop}
                      isDraggingColumn={draggingColumnId === column.id}
                      canCreateCard={canCreateCard}
                      canEditColumn={canEditItems}
                      canDeleteColumn={canDeleteItems}
                      canEditCard={canEditItems}
                      canDeleteCard={canDeleteItems}
                      canCloneCard={canCreateCard}
                      onTasksUpdate={reloadCurrentBoard}
                      inactivityTimerConfig={inactivityTimerConfig}
                    />
                  ))}
                </KanbanCardHeightProvider>
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      {/* Só monta aberto: fechado, ele percorria todos os cards a cada mudança do board. */}
      {boardReportOpen && (
        <BoardReportModal
          open={boardReportOpen}
          onClose={() => setBoardReportOpen(false)}
          board={currentBoard}
        />
      )}

      <BoardModal
        open={boardModalOpen}
        onClose={() => {
          setBoardModalOpen(false);
          setEditingBoard(null);
        }}
        board={editingBoard}
        onSuccess={() => {
          setBoardModalOpen(false);
          setEditingBoard(null);
          if (currentBoard && editingBoard && currentBoard.id === editingBoard.id) {
            reloadCurrentBoard();
          } else {
            // Com um board aberto, a lista atualiza por trás sem desmontá-lo.
            loadBoards({ silent: Boolean(currentBoard) });
          }
        }}
      />

      <ColumnModal
        open={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        column={currentColumn}
        boardId={currentBoard?.id}
        fieldDefinitions={normalizedBoardFields}
        onSuccess={() => {
          setColumnModalOpen(false);
          reloadCurrentBoard();
        }}
      />

      <CardModal
        open={cardModalOpen}
        onClose={() => setCardModalOpen(false)}
        card={currentCard}
        column={currentColumn}
        boardId={currentBoard?.id}
        board={currentBoard}
        onSuccess={() => {
          setCardModalOpen(false);
          reloadCurrentBoard();
        }}
      />

      <Dialog
        open={cloneModalOpen}
        onClose={resetCloneModalState}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enviar card para outro kanban</DialogTitle>
        <DialogContent>
          {cloneLoadingBoards ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <DialogContentText style={{ marginBottom: 12 }}>
                Selecione um board e uma coluna de destino para enviar o card com os mesmos dados do card atual.
              </DialogContentText>

              {!cloneBoards.length ? (
                <DialogContentText>
                  Nenhum board disponivel para envio. O card so pode ser enviado para outros boards.
                </DialogContentText>
              ) : (
                <>
                  <FormControl component="fieldset" margin="dense" fullWidth>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                      Ação
                    </Typography>
                    <RadioGroup
                      row
                      value={cloneTransferMode}
                      onChange={(e) => setCloneTransferMode(e.target.value)}
                    >
                      <FormControlLabel value="copy" control={<Radio color="primary" />} label="Copiar card" />
                      <FormControlLabel value="move" control={<Radio color="primary" />} label="Mover card" />
                    </RadioGroup>
                  </FormControl>

                  <FormControl variant="outlined" fullWidth margin="dense">
                    <InputLabel id="clone-board-label">Board de destino</InputLabel>
                    <Select
                      labelId="clone-board-label"
                      value={cloneSelectedBoardId}
                      onChange={(e) => setCloneSelectedBoardId(e.target.value)}
                      label="Board de destino"
                      disabled={cloneSubmitting}
                    >
                      <MenuItem value="">
                        <em>Selecione</em>
                      </MenuItem>
                      {cloneBoards.map((board) => (
                        <MenuItem key={board.id} value={board.id}>
                          {board.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="outlined" fullWidth margin="dense" disabled={!cloneSelectedBoardId}>
                    <InputLabel id="clone-column-label">Coluna de destino</InputLabel>
                    <Select
                      labelId="clone-column-label"
                      value={cloneSelectedColumnId}
                      onChange={(e) => setCloneSelectedColumnId(e.target.value)}
                      label="Coluna de destino"
                      disabled={!cloneSelectedBoardId || cloneSubmitting}
                    >
                      <MenuItem value="">
                        <em>Selecione</em>
                      </MenuItem>
                      {cloneColumns.map((column) => (
                        <MenuItem key={column.id} value={column.id}>
                          {column.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetCloneModalState} color="default" disabled={cloneSubmitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitCloneCard}
            color="primary"
            variant="contained"
            disabled={!cloneSelectedBoardId || !cloneSelectedColumnId || cloneSubmitting || !cloneBoards.length}
          >
            {cloneSubmitting
              ? "Enviando..."
              : cloneTransferMode === "move"
                ? "Mover card"
                : "Copiar card"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {itemToDelete?.type === "clear-column" ? "Confirmar Limpeza" : "Confirmar Exclusão"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {itemToDelete?.type === "board" ? (
              <>
                Tem certeza que deseja excluir o board <strong>{itemToDelete.name}</strong>?
                <br /><br />
                <strong>Atenção:</strong> Esta ação irá excluir permanentemente:
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>Todas as colunas do board</li>
                  <li>Todos os cards e seus anexos</li>
                  <li>Todo o histórico de atividades</li>
                </ul>
                <br />
                Esta ação não pode ser desfeita.
              </>
            ) : itemToDelete?.type === "clear-column" ? (
              <>
                Tem certeza que deseja limpar a coluna <strong>{itemToDelete.name}</strong>?
                <br /><br />
                <strong>Atenção:</strong> Esta ação irá excluir permanentemente todos os{" "}
                <strong>{itemToDelete.cards?.length || 0} cards</strong> contidos nesta coluna.
                <br /><br />
                Esta ação não pode ser desfeita.
              </>
            ) : (
              <>
                Tem certeza que deseja excluir este{" "}
                {itemToDelete?.type === "column" ? "coluna" : "card"}? Esta ação não
                pode ser desfeita.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="default">
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="secondary">
            {itemToDelete?.type === "clear-column" ? "Limpar" : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={requiredFieldsModalOpen}
        onClose={handleCancelRequiredFieldsMove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Campos obrigatorios da coluna</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 12 }}>
            {requiredFieldsMode === "clone"
              ? "Preencha os campos obrigatorios para concluir o envio do card na coluna de destino."
              : "Preencha os campos obrigatorios para concluir a movimentacao deste card para a coluna de destino."}
          </DialogContentText>
          {requiredFieldsToFill.map((field) => renderRequiredMoveField(field))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRequiredFieldsMove} color="default" disabled={savingRequiredFields}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmRequiredFieldsMove}
            color="primary"
            disabled={savingRequiredFields}
          >
            {savingRequiredFields
              ? "Salvando..."
              : requiredFieldsMode === "clone"
                ? "Salvar e enviar"
                : "Salvar e continuar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Reason Dialog */}
      <Dialog 
        open={moveReasonModalOpen} 
        onClose={handleCancelMoveWithReason}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {moveReasonModalMode === "clone" ? "Motivo do Envio" : "Motivo da Movimentação"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 16 }}>
            {moveReasonModalMode === "clone"
              ? "Por favor, informe o motivo do envio entre fluxos. Este registro será salvo no histórico do cliente ou do atendimento."
              : "Por favor, informe o motivo da mudança de coluna. Este registro será salvo no histórico do cliente ou do atendimento."}
          </DialogContentText>
          <TextField
            label="Motivo *"
            multiline
            rows={4}
            value={moveReason}
            onChange={handleMoveReasonChange}
            onPaste={handleMoveReasonPaste}
            fullWidth
            variant="outlined"
            margin="normal"
            inputProps={{
              style: {
                fontFamily: '"Roboto Mono", "Courier New", monospace',
                tabSize: 4,
                whiteSpace: "pre-wrap",
              },
            }}
            placeholder={
              moveReasonModalMode === "clone"
                ? "Descreva o motivo do envio do card para outro fluxo"
                : "Descreva o motivo da movimentação do card"
            }
            helperText="Este comentário será registrado com histórico de relacionamento"
            autoFocus
          />

          <Box mt={2}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={generateRelationshipOnMove}
                  onChange={(e) => setGenerateRelationshipOnMove(e.target.checked)}
                  color="primary"
                />
              )}
              label="Gerar relacionamento"
            />
          </Box>

          {generateRelationshipOnMove && (
            <Box mt={1} p={2} className={classes.relationshipFields}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                Dados do Relacionamento
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Data"
                    type="date"
                    size="small"
                    value={moveRelacionamentoData.data_contato}
                    onChange={(e) =>
                      setMoveRelacionamentoData({ ...moveRelacionamentoData, data_contato: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Hora"
                    type="time"
                    size="small"
                    value={moveRelacionamentoData.hora_contato}
                    onChange={(e) =>
                      setMoveRelacionamentoData({ ...moveRelacionamentoData, hora_contato: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Meio de Contato *</InputLabel>
                    <Select
                      value={moveRelacionamentoData.meio_contato}
                      onChange={(e) =>
                        setMoveRelacionamentoData({ ...moveRelacionamentoData, meio_contato: e.target.value })
                      }
                      label="Meio de Contato *"
                      disabled={enabledRelationshipContactMethods.length === 0}
                    >
                      {enabledRelationshipContactMethods.length === 0 && (
                        <MenuItem value="__no_contact_methods" disabled>
                          Nenhum meio de contato habilitado em Configurações &gt; Kanban
                        </MenuItem>
                      )}
                      {enabledRelationshipContactMethods.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <FormControl fullWidth size="small" disabled={moveContatos.length === 0}>
                      <InputLabel>Contato</InputLabel>
                      <Select
                        value={moveRelacionamentoData.contatoId}
                        onChange={(e) =>
                          setMoveRelacionamentoData({ ...moveRelacionamentoData, contatoId: e.target.value })
                        }
                        label="Contato"
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {moveContatos.length === 0 && (
                          <MenuItem value="__no_contacts" disabled>
                            Nenhum contato cadastrado
                          </MenuItem>
                        )}
                        {moveContatos.map((contato) => (
                          <MenuItem key={contato.id} value={contato.id}>
                            {contato.nome || contato.NOME}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {moveCardClienteId && (
                      <Tooltip title="Criar novo contato">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleOpenMoveContatoForm}
                        >
                          <PersonAddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={moveRegistrarFollowup}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          setMoveRegistrarFollowup(nextChecked);

                          if (!nextChecked) {
                            setMoveFollowupData(getDefaultMoveFollowupData());
                          }
                        }}
                        color="primary"
                      />
                    )}
                    label="Registrar follow-up"
                  />
                </Grid>

                {moveRegistrarFollowup && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        Dados do Follow-up
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Assunto do Follow-up *"
                        size="small"
                        value={moveFollowupData.assunto}
                        onChange={(e) => setMoveFollowupData({ ...moveFollowupData, assunto: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Data *"
                        type="date"
                        size="small"
                        value={moveFollowupData.data_followup}
                        onChange={(e) =>
                          setMoveFollowupData({ ...moveFollowupData, data_followup: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Hora *"
                        type="time"
                        size="small"
                        value={moveFollowupData.hora_followup}
                        onChange={(e) =>
                          setMoveFollowupData({ ...moveFollowupData, hora_followup: e.target.value })
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelMoveWithReason} color="default">
            Cancelar
          </Button>
          <Button onClick={handleConfirmMoveWithReason} color="primary" variant="contained">
            {moveReasonModalMode === "clone" ? "Confirmar e Enviar" : "Confirmar e Mover"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Contact Dialog (from Move Reason modal) */}
      <Dialog
        open={moveContatoFormOpen}
        onClose={handleCloseMoveContatoForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Novo Contato</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Nome do Contato *"
                fullWidth
                value={moveContatoFormData.nome}
                onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={moveContatoFormData.principal}
                    onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, principal: e.target.checked })}
                    color="primary"
                  />
                }
                label="Contato Principal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cargo"
                fullWidth
                value={moveContatoFormData.cargo}
                onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, cargo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="E-mail"
                fullWidth
                type="email"
                value={moveContatoFormData.email}
                onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="DDD"
                fullWidth
                value={moveContatoFormData.ddd_celular}
                onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, ddd_celular: e.target.value })}
                inputProps={{ maxLength: 3 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Celular"
                fullWidth
                value={moveContatoFormData.celular}
                onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, celular: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                placeholder="99999-9999"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="WhatsApp"
                fullWidth
                value={moveContatoFormData.whatsapp}
                onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, whatsapp: e.target.value })}
                placeholder="5542999999999"
                helperText="Código do país + DDD + número"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observações"
                fullWidth
                multiline
                rows={2}
                value={moveContatoFormData.observacoes}
                onChange={(e) => setMoveContatoFormData({ ...moveContatoFormData, observacoes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMoveContatoForm} disabled={savingMoveContato}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveMoveContato}
            color="primary"
            variant="contained"
            disabled={savingMoveContato}
          >
            {savingMoveContato ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Move Dialog */}
      <Dialog 
        open={scheduleMoveModalOpen} 
        onClose={handleCancelScheduleMove}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agendamento Obrigatório</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 16 }}>
            Para mover este card para outra coluna, é necessário criar um agendamento.
          </DialogContentText>
          <TextField
            label="Data do Agendamento *"
            type="datetime-local"
            value={moveScheduleDate}
            onChange={(e) => setMoveScheduleDate(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Informe a data e hora para o agendamento"
          />
          <TextField
            label="Observação *"
            multiline
            rows={4}
            value={moveScheduleDescription}
            onChange={(e) => setMoveScheduleDescription(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            placeholder="Descreva o motivo da movimentação ou detalhes do agendamento"
            helperText="Esta observação será registrada no agendamento"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelScheduleMove} color="default">
            Cancelar
          </Button>
          <Button onClick={handleConfirmScheduleMove} color="primary" variant="contained">
            Confirmar e Mover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Board Menu */}
      <Menu
        anchorEl={boardMenuAnchor}
        open={Boolean(boardMenuAnchor)}
        onClose={handleBoardMenuClose}
      >
        {canEditBoard && (
          <MenuItem onClick={handleEditCurrentBoard}>
            <EditIcon fontSize="small" style={{ marginRight: 8 }} />
            Renomear Board
          </MenuItem>
        )}
        {canDeleteBoard && (
          <MenuItem onClick={handleDeleteCurrentBoard}>
            <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
            Excluir Board
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default KanbanBoard;
